import React, { useState, useEffect, useCallback, useRef } from 'react';
import AccountStatementContextMenu, { AccountStatementContextMenuHandle } from '../../components/accountStatement/AccountStatementContextMenu';
import config from '../../config';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Box, Paper, Typography, TextField, MenuItem, Button, CircularProgress, Card, CardContent,
  IconButton, Tooltip, useTheme, Tabs, Tab, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Divider, Fade, Grid, Autocomplete,
} from '@mui/material';
import {
  Refresh as RefreshIcon, FileDownload as ExcelIcon, PictureAsPdf as PdfIcon,
  Print as PrintIcon, Assessment, TrendingUp, TrendingDown, AccountBalance,
  AttachMoney, MoneyOff, Savings, ShowChart, PieChart, BarChart,
  CheckCircle, Error as ErrorIcon, SwapVert, FileDownload,
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  financialStatementsService, FinancialStatementFilter,
  IncomeStatementResult, BalanceSheetResult, CashFlowStatementResult, IncomeStatementLineDto
} from '../../services/financialReports.service';
import { fiscalPeriodsService } from '../../services/fiscalPeriods.service';
import { companyInfoService } from '../../services/companyInfo.service';
import { toast } from 'react-toastify';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useFiscalDateRange } from '../../hooks/useFiscalDateRange';

interface FiscalPeriod { fiscalPeriodId: number; periodName: string; startDate: string; endDate: string; isCurrent: boolean; }
interface CompanyInfo { companyName?: string; companyNameAr?: string; logoBase64?: string | null; logoPath?: string | null; address?: string; phone?: string; email?: string; website?: string; }
interface TabPanelProps { children?: React.ReactNode; index: number; value: number; }

const TabPanel = ({ children, value, index }: TabPanelProps) => (
  <div hidden={value !== index} style={{ height: '100%' }}>{value === index && children}</div>
);

const translations = {
  en: {
    title: 'Financial Statements',
    subtitle: 'Income Statement, Balance Sheet & Cash Flow',
    incomeStatement: 'Income Statement',
    balanceSheet: 'Balance Sheet',
    cashFlow: 'Cash Flow',
    fiscalPeriod: 'Fiscal Period',
    fromDate: 'From Date',
    toDate: 'To Date',
    refresh: 'Generate',
    // Income Statement
    clearFilters: 'Clear',
    revenue: 'Revenue',
    cogs: 'Cost of Goods Sold',
    totalCOGS: 'Total COGS',
    expenses: 'Expenses',
    operatingExpenses: 'Operating Expenses',
    otherIncome: 'Other Income',
    otherExpenses: 'Other Expenses',
    totalRevenue: 'Total Revenue',
    totalExpenses: 'Total Expenses',
    totalOperatingExpenses: 'Total Operating Expenses',
    totalOtherIncome: 'Total Other Income',
    totalOtherExpenses: 'Total Other Expenses',
    grossProfit: 'Gross Profit',
    operatingIncome: 'Operating Income',
    incomeBeforeTax: 'Income Before Tax',
    taxExpense: 'Zakat & Contributions',
    netIncome: 'Net Income',
    netLoss: 'Net Loss',
    // Balance Sheet
    assets: 'Assets',
    currentAssets: 'Current Assets',
    fixedAssets: 'Fixed Assets',
    totalCurrentAssets: 'Total Current Assets',
    totalFixedAssets: 'Total Fixed Assets',
    totalAssets: 'Total Assets',
    liabilities: 'Liabilities',
    currentLiabilities: 'Current Liabilities',
    longTermLiabilities: 'Long-term Liabilities',
    totalLiabilities: 'Total Liabilities',
    equity: 'Equity',
    totalEquity: 'Total Equity',
    retainedEarnings: 'Retained Earnings',
    currentYearIncome: 'Current Year Net Income',
    totalLiabAndEquity: 'Total Liabilities & Equity',
    balanced: 'Balanced',
    notBalanced: 'Not Balanced',
    difference: 'Difference',
    // Cash Flow
    operatingActivities: 'Operating Activities',
    investingActivities: 'Investing Activities',
    financingActivities: 'Financing Activities',
    netCashOperating: 'Net Cash from Operating',
    netCashInvesting: 'Net Cash from Investing',
    netCashFinancing: 'Net Cash from Financing',
    netChangeInCash: 'Net Change in Cash',
    beginningCash: 'Beginning Cash Balance',
    endingCash: 'Ending Cash Balance',
    // Common
    accountCode: 'Code',
    accountName: 'Account',
    amount: 'Amount',
    balance: 'Balance',
    noData: 'Select a fiscal period and dates, then click "Generate"',
    loading: 'Loading...',
    kwd: 'KWD',
    generatedAt: 'Generated at',
    period: 'Period',
    exportOptions: 'Export Options',
    selectStatements: 'Select Statements to Export/Print',
    selectAll: 'Select All',
    deselectAll: 'Deselect All',
  },
  ar: {
    title: 'القوائم المالية',
    subtitle: 'قائمة الدخل، الميزانية العمومية وقائمة التدفقات النقدية',
    incomeStatement: 'قائمة الدخل',
    balanceSheet: 'الميزانية العمومية',
    cashFlow: 'التدفقات النقدية',
    fiscalPeriod: 'السنة المالية',
    fromDate: 'من تاريخ',
    toDate: 'إلى تاريخ',
    refresh: 'إنشاء',
    // Income Statement
    revenue: 'الإيرادات',
    cogs: 'تكلفة البضاعة المباعة',
    totalCOGS: 'إجمالي تكلفة البضاعة المباعة',
    expenses: 'المصروفات',
    operatingExpenses: 'المصروفات التشغيلية',
    otherIncome: 'إيرادات أخرى',
    otherExpenses: 'مصروفات أخرى',
    totalRevenue: 'إجمالي الإيرادات',
    totalExpenses: 'إجمالي المصروفات',
    totalOperatingExpenses: 'إجمالي المصروفات التشغيلية',
    totalOtherIncome: 'إجمالي الإيرادات الأخرى',
    totalOtherExpenses: 'إجمالي المصروفات الأخرى',
    grossProfit: 'إجمالي الربح',
    operatingIncome: 'الدخل التشغيلي',
    incomeBeforeTax: 'الدخل قبل الضريبة',
    taxExpense: 'الزكاة والمساهمات',
    netIncome: 'صافي الربح',
    netLoss: 'صافي الخسارة',
    // Balance Sheet
    assets: 'الأصول',
    currentAssets: 'الأصول المتداولة',
    fixedAssets: 'الأصول الثابتة',
    totalCurrentAssets: 'إجمالي الأصول المتداولة',
    totalFixedAssets: 'إجمالي الأصول الثابتة',
    totalAssets: 'إجمالي الأصول',
    liabilities: 'الالتزامات',
    currentLiabilities: 'الالتزامات المتداولة',
    longTermLiabilities: 'الالتزامات طويلة الأجل',
    totalLiabilities: 'إجمالي الالتزامات',
    equity: 'حقوق الملكية',
    totalEquity: 'إجمالي حقوق الملكية',
    retainedEarnings: 'الأرباح المحتجزة',
    currentYearIncome: 'صافي دخل السنة الحالية',
    totalLiabAndEquity: 'إجمالي الالتزامات وحقوق الملكية',
    balanced: 'متوازن',
    notBalanced: 'غير متوازن',
    difference: 'الفرق',
    // Cash Flow
    operatingActivities: 'الأنشطة التشغيلية',
    investingActivities: 'الأنشطة الاستثمارية',
    financingActivities: 'الأنشطة التمويلية',
    netCashOperating: 'صافي النقد من التشغيل',
    netCashInvesting: 'صافي النقد من الاستثمار',
    netCashFinancing: 'صافي النقد من التمويل',
    netChangeInCash: 'صافي التغير في النقد',
    beginningCash: 'رصيد النقد أول الفترة',
    endingCash: 'رصيد النقد آخر الفترة',
    // Common
    accountCode: 'الكود',
    accountName: 'الحساب',
    amount: 'المبلغ',
    balance: 'الرصيد',
    noData: 'اختر السنة المالية والتواريخ واضغط "إنشاء"',
    loading: 'جاري التحميل...',
    kwd: 'د.ك',
    generatedAt: 'تاريخ التقرير',
    period: 'الفترة',
    exportOptions: 'خيارات التصدير',
    selectStatements: 'اختر القوائم للتصدير/الطباعة',
    clearFilters: 'مسح',
    selectAll: 'تحديد الكل',
    deselectAll: 'إلغاء تحديد الكل',
  },
};

const FinancialStatementsPage: React.FC = () => {
  const { language } = useLanguage();
  const theme = useTheme();
  const t = translations[language];
  const isRTL = language === 'ar';
  const isDark = theme.palette.mode === 'dark';
  const { minDate, maxDate } = useFiscalDateRange();

  // State
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [fiscalPeriods, setFiscalPeriods] = useState<FiscalPeriod[]>([]);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [fiscalPeriodId, setFiscalPeriodId] = useState<number | undefined>();
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');

  // Export Options
  const [exportOptions, setExportOptions] = useState({
    incomeStatement: true,
    balanceSheet: true,
    cashFlow: true
  });

  // Data
  const [incomeData, setIncomeData] = useState<IncomeStatementResult | null>(null);
  const [balanceData, setBalanceData] = useState<BalanceSheetResult | null>(null);
  const [cashFlowData, setCashFlowData] = useState<CashFlowStatementResult | null>(null);

  useEffect(() => { loadFiscalPeriods(); loadCompanyInfo(); }, []);

  const loadFiscalPeriods = async () => {
    try {
      const periods = await fiscalPeriodsService.getAll();
      setFiscalPeriods(periods);
      const current = periods.find((p: FiscalPeriod) => p.isCurrent);
      if (current) {
        setFiscalPeriodId(current.fiscalPeriodId);
        setFromDate(current.startDate.split('T')[0]);
        setToDate(current.endDate.split('T')[0]);
      }
    } catch (error) { console.error('Error:', error); }
  };

  const loadCompanyInfo = async () => {
    try { setCompanyInfo(await companyInfoService.getCompanyInfo()); } 
    catch (error) { console.error('Error:', error); }
  };

  const generateReports = useCallback(async () => {
    if (!fiscalPeriodId) { toast.warning(isRTL ? 'اختر السنة المالية' : 'Select fiscal period'); return; }
    if (!fromDate || !toDate) { toast.warning(isRTL ? 'اختر الفترة الزمنية' : 'Select date range'); return; }
    
    setLoading(true);
    try {
      const filter: FinancialStatementFilter = { 
        fiscalPeriodId, 
        fromDate, 
        toDate 
      };
      
      const [income, balance, cashFlow] = await Promise.all([
        financialStatementsService.getIncomeStatement(filter),
        financialStatementsService.getBalanceSheet(filter),
        financialStatementsService.getCashFlowStatement(filter),
      ]);

      setIncomeData(income);
      setBalanceData(balance);
      setCashFlowData(cashFlow);
      toast.success(isRTL ? 'تم إنشاء القوائم المالية' : 'Financial statements generated');
    } catch (error: any) {
      console.error('Error:', error);
      toast.error(error.response?.data?.message || 'Error generating reports');
    } finally {
      setLoading(false);
    }
  }, [fiscalPeriodId, fromDate, toDate, isRTL]);

  const formatNumber = (num: number) => num.toLocaleString('en-GB', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
  const formatDate = (dateStr?: string) => dateStr ? new Date(dateStr).toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB') : '';

  // ✅ Account statement context menu (right-click on any account row)
  const acctMenuRef = useRef<AccountStatementContextMenuHandle>(null);
  const handleAccountContextMenu = (e: React.MouseEvent, line: any) => {
    if (line.isHeader || !line.accountId) return;
    acctMenuRef.current?.open(e, {
      accountId: line.accountId,
      accountCode: line.accountCode,
      accountName: line.accountName,
      accountNameAr: line.accountNameAr,
    });
  };

  // ================== INCOME STATEMENT TAB ==================
  const renderIncomeStatement = () => {
    if (!incomeData) return <Box sx={{ textAlign: 'center', p: 5 }}><Assessment sx={{ fontSize: 60, color: 'action.disabled', mb: 2 }} /><Typography color="textSecondary">{t.noData}</Typography></Box>;

    const isProfit = incomeData.netIncome >= 0;

    return (
      <Fade in>
        <Box>
          {/* Summary Cards */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            <Card sx={{ flex: '1 1 180px', bgcolor: isDark ? '#1b4332' : '#e8f5e9' }}>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <TrendingUp sx={{ fontSize: 28, color: 'success.main' }} />
                <Typography variant="caption" color="textSecondary" fontSize="0.7rem">{t.totalRevenue}</Typography>
                <Typography variant="h6" fontWeight="bold" color="success.main">{formatNumber(incomeData.totalRevenue)}</Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: '1 1 180px', bgcolor: isDark ? '#4a1515' : '#fff3e0' }}>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <MoneyOff sx={{ fontSize: 28, color: 'warning.main' }} />
                <Typography variant="caption" color="textSecondary" fontSize="0.7rem">{t.totalCOGS}</Typography>
                <Typography variant="h6" fontWeight="bold" color="warning.main">{formatNumber(incomeData.totalCOGS)}</Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: '1 1 180px', bgcolor: isDark ? '#1b4332' : '#c8e6c9' }}>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <ShowChart sx={{ fontSize: 28, color: 'success.dark' }} />
                <Typography variant="caption" color="textSecondary" fontSize="0.7rem">{t.grossProfit}</Typography>
                <Typography variant="h6" fontWeight="bold" color="success.dark">{formatNumber(incomeData.grossProfit)}</Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: '1 1 180px', bgcolor: isDark ? '#4a1515' : '#ffebee' }}>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <TrendingDown sx={{ fontSize: 28, color: 'error.main' }} />
                <Typography variant="caption" color="textSecondary" fontSize="0.7rem">{t.totalOperatingExpenses}</Typography>
                <Typography variant="h6" fontWeight="bold" color="error.main">{formatNumber(incomeData.totalOperatingExpenses)}</Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: '1 1 250px', bgcolor: isProfit ? (isDark ? '#1b4332' : '#c8e6c9') : (isDark ? '#4a1515' : '#ffcdd2'), border: '3px solid', borderColor: isProfit ? 'success.main' : 'error.main' }}>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                {isProfit ? <Savings sx={{ fontSize: 36, color: 'success.main' }} /> : <MoneyOff sx={{ fontSize: 36, color: 'error.main' }} />}
                <Typography variant="caption" color="textSecondary" fontSize="0.75rem">{isProfit ? t.netIncome : t.netLoss}</Typography>
                <Typography variant="h5" fontWeight="bold" color={isProfit ? 'success.main' : 'error.main'}>{formatNumber(Math.abs(incomeData.netIncome))}</Typography>
                <Chip label={isProfit ? t.netIncome : t.netLoss} color={isProfit ? 'success' : 'error'} size="small" sx={{ mt: 0.5, fontSize: '0.7rem' }} />
              </CardContent>
            </Card>
          </Box>

          {/* Income Statement Table */}
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.accountCode}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.accountName}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold', width: 150 }}>{t.amount}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* 1. Revenue Section */}
                {incomeData.revenueLines && incomeData.revenueLines.length > 0 && (
                  <>
                    <TableRow sx={{ bgcolor: isDark ? '#1b4332' : '#e8f5e9' }}>
                      <TableCell colSpan={3} sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                        <TrendingUp sx={{ mr: 1, verticalAlign: 'middle', color: 'success.main' }} />
                        {t.revenue}
                      </TableCell>
                    </TableRow>
                    {incomeData.revenueLines.map((line: IncomeStatementLineDto) => (
                      <TableRow key={`rev-${line.accountId}`} hover={!line.isHeader} onContextMenu={(e) => handleAccountContextMenu(e, line)} sx={line.isHeader ? { bgcolor: isDark ? '#1b4332' : '#f1f8e9' } : { cursor: 'context-menu' }}>
                        <TableCell sx={{ pl: line.isHeader ? 3 : 6, fontWeight: line.isHeader ? 'bold' : 'normal' }}>{line.accountCode}</TableCell>
                        <TableCell sx={{ fontWeight: line.isHeader ? 'bold' : 'normal' }}>{isRTL ? line.accountNameAr : line.accountName}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', color: 'success.main', fontWeight: line.isHeader ? 'bold' : 'normal' }}>{formatNumber(line.currentPeriodAmount)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: isDark ? '#2e7d32' : '#a5d6a7' }}>
                      <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>{t.totalRevenue}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold', fontSize: '1.1rem' }}>{formatNumber(incomeData.totalRevenue)}</TableCell>
                    </TableRow>
                  </>
                )}

                {/* 2. COGS Section */}
                {incomeData.cogsLines && incomeData.cogsLines.length > 0 && (
                  <>
                    <TableRow sx={{ bgcolor: isDark ? '#4a1515' : '#fff3e0' }}>
                      <TableCell colSpan={3} sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                        <MoneyOff sx={{ mr: 1, verticalAlign: 'middle', color: 'warning.main' }} />
                        {t.cogs}
                      </TableCell>
                    </TableRow>
                    {incomeData.cogsLines.map((line: IncomeStatementLineDto) => (
                      <TableRow key={`cogs-${line.accountId}`} hover={!line.isHeader} onContextMenu={(e) => handleAccountContextMenu(e, line)} sx={line.isHeader ? { bgcolor: isDark ? '#4a1515' : '#fff8e1' } : { cursor: 'context-menu' }}>
                        <TableCell sx={{ pl: line.isHeader ? 3 : 6, fontWeight: line.isHeader ? 'bold' : 'normal' }}>{line.accountCode}</TableCell>
                        <TableCell sx={{ fontWeight: line.isHeader ? 'bold' : 'normal' }}>{isRTL ? line.accountNameAr : line.accountName}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', color: 'warning.main', fontWeight: line.isHeader ? 'bold' : 'normal' }}>{formatNumber(line.currentPeriodAmount)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: isDark ? '#f57c00' : '#ffe0b2' }}>
                      <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>{t.totalCOGS}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold', fontSize: '1.1rem' }}>{formatNumber(incomeData.totalCOGS)}</TableCell>
                    </TableRow>
                  </>
                )}

                {/* 3. Gross Profit */}
                {(incomeData.grossProfit !== undefined && incomeData.grossProfit !== 0) && (
                  <TableRow sx={{ bgcolor: isDark ? '#2e7d32' : '#c8e6c9' }}>
                    <TableCell colSpan={2} sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: isDark ? 'white' : 'success.dark' }}>
                      <ShowChart sx={{ mr: 1, verticalAlign: 'middle' }} />
                      {t.grossProfit}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold', fontSize: '1.2rem', color: isDark ? 'white' : 'success.dark' }}>
                      {formatNumber(incomeData.grossProfit)}
                    </TableCell>
                  </TableRow>
                )}

                {/* 4. Operating Expenses Section */}
                {incomeData.operatingExpenseLines && incomeData.operatingExpenseLines.length > 0 && (
                  <>
                    <TableRow sx={{ bgcolor: isDark ? '#4a1515' : '#ffebee' }}>
                      <TableCell colSpan={3} sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                        <TrendingDown sx={{ mr: 1, verticalAlign: 'middle', color: 'error.main' }} />
                        {t.operatingExpenses}
                      </TableCell>
                    </TableRow>
                    {incomeData.operatingExpenseLines.map((line: IncomeStatementLineDto) => (
                      <TableRow key={`opex-${line.accountId}`} hover={!line.isHeader} onContextMenu={(e) => handleAccountContextMenu(e, line)} sx={line.isHeader ? { bgcolor: isDark ? '#4a1515' : '#fce4ec' } : { cursor: 'context-menu' }}>
                        <TableCell sx={{ pl: line.isHeader ? 3 : 6, fontWeight: line.isHeader ? 'bold' : 'normal' }}>{line.accountCode}</TableCell>
                        <TableCell sx={{ fontWeight: line.isHeader ? 'bold' : 'normal' }}>{isRTL ? line.accountNameAr : line.accountName}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', color: 'error.main', fontWeight: line.isHeader ? 'bold' : 'normal' }}>{formatNumber(line.currentPeriodAmount)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: isDark ? '#c62828' : '#ef9a9a' }}>
                      <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>{t.totalOperatingExpenses}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold', fontSize: '1.1rem' }}>{formatNumber(incomeData.totalOperatingExpenses)}</TableCell>
                    </TableRow>
                  </>
                )}

                {/* 5. Operating Income */}
                {(incomeData.operatingIncome !== undefined && incomeData.operatingIncome !== 0) && (
                  <TableRow sx={{ bgcolor: isDark ? '#1565c0' : '#90caf9' }}>
                    <TableCell colSpan={2} sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: isDark ? 'white' : 'primary.dark' }}>
                      <AccountBalance sx={{ mr: 1, verticalAlign: 'middle' }} />
                      {t.operatingIncome}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold', fontSize: '1.2rem', color: isDark ? 'white' : 'primary.dark' }}>
                      {formatNumber(incomeData.operatingIncome)}
                    </TableCell>
                  </TableRow>
                )}

                {/* 6. Other Income */}
                {incomeData.otherIncomeLines && incomeData.otherIncomeLines.length > 0 && (
                  <>
                    <TableRow sx={{ bgcolor: isDark ? '#1b4332' : '#e0f2f1' }}>
                      <TableCell colSpan={3} sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>
                        <AttachMoney sx={{ mr: 1, verticalAlign: 'middle', color: 'success.light' }} />
                        {t.otherIncome}
                      </TableCell>
                    </TableRow>
                    {incomeData.otherIncomeLines.map((line: IncomeStatementLineDto) => (
                      <TableRow key={`oi-${line.accountId}`} hover={!line.isHeader} onContextMenu={(e) => handleAccountContextMenu(e, line)} sx={line.isHeader ? { bgcolor: isDark ? '#1b4332' : '#e8f5e9' } : { cursor: 'context-menu' }}>
                        <TableCell sx={{ pl: line.isHeader ? 3 : 6, fontWeight: line.isHeader ? 'bold' : 'normal' }}>{line.accountCode}</TableCell>
                        <TableCell sx={{ fontWeight: line.isHeader ? 'bold' : 'normal' }}>{isRTL ? line.accountNameAr : line.accountName}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', color: 'success.light', fontWeight: line.isHeader ? 'bold' : 'normal' }}>{formatNumber(line.currentPeriodAmount)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: isDark ? '#388e3c' : '#c8e6c9' }}>
                      <TableCell colSpan={2} sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>{t.totalOtherIncome}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{formatNumber(incomeData.totalOtherIncome)}</TableCell>
                    </TableRow>
                  </>
                )}

                {/* 7. Other Expenses */}
                {incomeData.otherExpenseLines && incomeData.otherExpenseLines.length > 0 && (
                  <>
                    <TableRow sx={{ bgcolor: isDark ? '#4a1515' : '#fce4ec' }}>
                      <TableCell colSpan={3} sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>
                        <MoneyOff sx={{ mr: 1, verticalAlign: 'middle', color: 'error.light' }} />
                        {t.otherExpenses}
                      </TableCell>
                    </TableRow>
                    {incomeData.otherExpenseLines.map((line: IncomeStatementLineDto) => (
                      <TableRow key={`oe-${line.accountId}`} hover={!line.isHeader} onContextMenu={(e) => handleAccountContextMenu(e, line)} sx={line.isHeader ? { bgcolor: isDark ? '#4a1515' : '#fce4ec' } : { cursor: 'context-menu' }}>
                        <TableCell sx={{ pl: line.isHeader ? 3 : 6, fontWeight: line.isHeader ? 'bold' : 'normal' }}>{line.accountCode}</TableCell>
                        <TableCell sx={{ fontWeight: line.isHeader ? 'bold' : 'normal' }}>{isRTL ? line.accountNameAr : line.accountName}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', color: 'error.light', fontWeight: line.isHeader ? 'bold' : 'normal' }}>{formatNumber(line.currentPeriodAmount)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: isDark ? '#d32f2f' : '#ffcdd2' }}>
                      <TableCell colSpan={2} sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>{t.totalOtherExpenses}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{formatNumber(incomeData.totalOtherExpenses)}</TableCell>
                    </TableRow>
                  </>
                )}

                {/* 8. Income Before Tax */}
                {(incomeData.incomeBeforeTax !== undefined) && (
                  <TableRow sx={{ bgcolor: isDark ? '#1565c0' : '#e3f2fd' }}>
                    <TableCell colSpan={2} sx={{ fontWeight: 'bold', fontSize: '1rem' }}>{t.incomeBeforeTax}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold', fontSize: '1.1rem' }}>{formatNumber(incomeData.incomeBeforeTax)}</TableCell>
                  </TableRow>
                )}

                {/* 9. Tax Expense */}
                {(incomeData.taxExpense !== undefined && incomeData.taxExpense !== 0) && (
                  <TableRow hover>
                    <TableCell colSpan={2} sx={{ pl: 4, fontStyle: 'italic' }}>{t.taxExpense}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{formatNumber(incomeData.taxExpense)}</TableCell>
                  </TableRow>
                )}

                {/* 10. Net Income */}
                <TableRow sx={{ bgcolor: isProfit ? '#4caf50' : '#f44336' }}>
                  <TableCell colSpan={2} sx={{ fontWeight: 'bold', fontSize: '1.2rem', color: 'white' }}>
                    {isProfit ? <Savings sx={{ mr: 1, verticalAlign: 'middle' }} /> : <MoneyOff sx={{ mr: 1, verticalAlign: 'middle' }} />}
                    {isProfit ? t.netIncome : t.netLoss}
                  </TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold', fontSize: '1.3rem', color: 'white' }}>
                    {formatNumber(Math.abs(incomeData.netIncome))}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Fade>
    );
  };

  // ================== BALANCE SHEET TAB ==================
  const renderBalanceSheet = () => {
    if (!balanceData) return <Box sx={{ textAlign: 'center', p: 5 }}><AccountBalance sx={{ fontSize: 60, color: 'action.disabled', mb: 2 }} /><Typography color="textSecondary">{t.noData}</Typography></Box>;

    return (
      <Fade in>
        <Box>
          {/* Balance Status */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Chip
              icon={balanceData.isBalanced ? <CheckCircle /> : <ErrorIcon />}
              label={`${balanceData.isBalanced ? t.balanced : t.notBalanced} ${!balanceData.isBalanced ? `(${t.difference}: ${formatNumber(balanceData.difference)})` : ''}`}
              color={balanceData.isBalanced ? 'success' : 'error'}
              size="medium"
              sx={{ fontSize: '1rem', py: 2.5 }}
            />
          </Box>

          {/* Summary Cards */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            <Card sx={{ flex: '1 1 200px', bgcolor: isDark ? '#1565c0' : '#bbdefb' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <ShowChart sx={{ fontSize: 32, color: 'primary.main' }} />
                <Typography variant="caption" color="textSecondary">{t.totalAssets}</Typography>
                <Typography variant="h5" fontWeight="bold" color="primary.main">{formatNumber(balanceData.totalAssets)}</Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: '1 1 200px', bgcolor: isDark ? '#4a1515' : '#ffcdd2' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <MoneyOff sx={{ fontSize: 32, color: 'error.main' }} />
                <Typography variant="caption" color="textSecondary">{t.totalLiabilities}</Typography>
                <Typography variant="h5" fontWeight="bold" color="error.main">{formatNumber(balanceData.totalLiabilities)}</Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: '1 1 200px', bgcolor: isDark ? '#1b4332' : '#c8e6c9' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Savings sx={{ fontSize: 32, color: 'success.main' }} />
                <Typography variant="caption" color="textSecondary">{t.totalEquity}</Typography>
                <Typography variant="h5" fontWeight="bold" color="success.main">{formatNumber(balanceData.totalEquity)}</Typography>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {/* Assets Column */}
            <TableContainer component={Paper} sx={{ flex: '1 1 400px' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#1565c0' }}>
                    <TableCell colSpan={2} sx={{ color: 'white', fontWeight: 'bold', fontSize: '1rem' }}>
                      <ShowChart sx={{ mr: 1, verticalAlign: 'middle' }} />
                      {t.assets}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Current Assets */}
                  <TableRow sx={{ bgcolor: isDark ? '#1e3a5f' : '#e3f2fd' }}>
                    <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>{t.currentAssets}</TableCell>
                  </TableRow>
                  {balanceData.currentAssets.map(item => (
                    <TableRow key={`ca-${item.accountId}`} hover={!item.isHeader} onContextMenu={(e) => handleAccountContextMenu(e, item)} sx={item.isHeader ? { bgcolor: isDark ? '#1e3a5f' : '#e8f0fe' } : { cursor: 'context-menu' }}>
                      <TableCell sx={{ pl: item.isHeader ? 2 : 5, fontWeight: item.isHeader ? 'bold' : 'normal' }}>{isRTL ? item.accountNameAr : item.accountName}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: item.isHeader ? 'bold' : 'normal' }}>{formatNumber(item.currentPeriodBalance)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ bgcolor: isDark ? '#0d47a1' : '#90caf9' }}>
                    <TableCell sx={{ fontWeight: 'bold', pl: 2 }}>{t.totalAssets} ({t.currentAssets})</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{formatNumber(balanceData.totalCurrentAssets)}</TableCell>
                  </TableRow>

                  {/* Fixed Assets */}
                  {balanceData.fixedAssets.length > 0 && (
                    <>
                      <TableRow sx={{ bgcolor: isDark ? '#1e3a5f' : '#e3f2fd' }}>
                        <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>{t.fixedAssets}</TableCell>
                      </TableRow>
                      {balanceData.fixedAssets.map(item => (
                        <TableRow key={`fa-${item.accountId}`} hover={!item.isHeader} onContextMenu={(e) => handleAccountContextMenu(e, item)} sx={item.isHeader ? { bgcolor: isDark ? '#1e3a5f' : '#e8f0fe' } : { cursor: 'context-menu' }}>
                          <TableCell sx={{ pl: item.isHeader ? 2 : 5, fontWeight: item.isHeader ? 'bold' : 'normal' }}>{isRTL ? item.accountNameAr : item.accountName}</TableCell>
                          <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: item.isHeader ? 'bold' : 'normal' }}>{formatNumber(item.currentPeriodBalance)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ bgcolor: isDark ? '#0d47a1' : '#90caf9' }}>
                        <TableCell sx={{ fontWeight: 'bold', pl: 2 }}>{t.totalAssets} ({t.fixedAssets})</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{formatNumber(balanceData.totalFixedAssets)}</TableCell>
                      </TableRow>
                    </>
                  )}

                  {/* Total Assets */}
                  <TableRow sx={{ bgcolor: '#1565c0' }}>
                    <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>{t.totalAssets}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold', fontSize: '1.1rem', color: 'white' }}>{formatNumber(balanceData.totalAssets)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            {/* Liabilities & Equity Column */}
            <TableContainer component={Paper} sx={{ flex: '1 1 400px' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#c62828' }}>
                    <TableCell colSpan={2} sx={{ color: 'white', fontWeight: 'bold', fontSize: '1rem' }}>
                      <MoneyOff sx={{ mr: 1, verticalAlign: 'middle' }} />
                      {t.liabilities}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Current Liabilities */}
                  <TableRow sx={{ bgcolor: isDark ? '#4a1515' : '#ffebee' }}>
                    <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>{t.currentLiabilities}</TableCell>
                  </TableRow>
                  {balanceData.currentLiabilities.map(item => (
                    <TableRow key={`cl-${item.accountId}`} hover={!item.isHeader} onContextMenu={(e) => handleAccountContextMenu(e, item)} sx={item.isHeader ? { bgcolor: isDark ? '#4a1515' : '#fce4ec' } : { cursor: 'context-menu' }}>
                      <TableCell sx={{ pl: item.isHeader ? 2 : 5, fontWeight: item.isHeader ? 'bold' : 'normal' }}>{isRTL ? item.accountNameAr : item.accountName}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: item.isHeader ? 'bold' : 'normal' }}>{formatNumber(item.currentPeriodBalance)}</TableCell>
                    </TableRow>
                  ))}
                  {balanceData.totalCurrentLiabilities !== 0 && (
                    <TableRow sx={{ bgcolor: isDark ? '#7f1d1d' : '#ffcdd2' }}>
                      <TableCell sx={{ fontWeight: 'bold', pl: 2 }}>{t.totalLiabilities} ({t.currentLiabilities})</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{formatNumber(balanceData.totalCurrentLiabilities)}</TableCell>
                    </TableRow>
                  )}

                  {/* Long-term Liabilities */}
                  {balanceData.longTermLiabilities && balanceData.longTermLiabilities.length > 0 && (
                    <>
                      <TableRow sx={{ bgcolor: isDark ? '#4a1515' : '#ffebee' }}>
                        <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>{t.longTermLiabilities}</TableCell>
                      </TableRow>
                      {balanceData.longTermLiabilities.map(item => (
                        <TableRow key={`lt-${item.accountId}`} hover={!item.isHeader} onContextMenu={(e) => handleAccountContextMenu(e, item)} sx={item.isHeader ? { bgcolor: isDark ? '#4a1515' : '#fce4ec' } : { cursor: 'context-menu' }}>
                          <TableCell sx={{ pl: item.isHeader ? 2 : 5, fontWeight: item.isHeader ? 'bold' : 'normal' }}>{isRTL ? item.accountNameAr : item.accountName}</TableCell>
                          <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: item.isHeader ? 'bold' : 'normal' }}>{formatNumber(item.currentPeriodBalance)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ bgcolor: isDark ? '#7f1d1d' : '#ffcdd2' }}>
                        <TableCell sx={{ fontWeight: 'bold', pl: 2 }}>{t.totalLiabilities} ({t.longTermLiabilities})</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{formatNumber(balanceData.totalLongTermLiabilities)}</TableCell>
                      </TableRow>
                    </>
                  )}

                  <TableRow sx={{ bgcolor: isDark ? '#b71c1c' : '#ef9a9a' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t.totalLiabilities}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{formatNumber(balanceData.totalLiabilities)}</TableCell>
                  </TableRow>

                  {/* Equity */}
                  <TableRow sx={{ bgcolor: '#2e7d32' }}>
                    <TableCell colSpan={2} sx={{ color: 'white', fontWeight: 'bold', fontSize: '1rem' }}>
                      <Savings sx={{ mr: 1, verticalAlign: 'middle' }} />
                      {t.equity}
                    </TableCell>
                  </TableRow>
                  {balanceData.equityItems.map(item => (
                    <TableRow key={`eq-${item.accountId}`} hover={!item.isHeader} onContextMenu={(e) => handleAccountContextMenu(e, item)} sx={item.isHeader ? { bgcolor: isDark ? '#1b4332' : '#e8f5e9' } : { cursor: 'context-menu' }}>
                      <TableCell sx={{ pl: item.isHeader ? 2 : 5, fontWeight: item.isHeader ? 'bold' : 'normal' }}>{isRTL ? item.accountNameAr : item.accountName}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: item.isHeader ? 'bold' : 'normal' }}>{formatNumber(item.currentPeriodBalance)}</TableCell>
                    </TableRow>
                  ))}
                  {balanceData.currentYearNetIncome !== 0 && (
                    <TableRow sx={{ bgcolor: isDark ? '#1b5e20' : '#a5d6a7' }}>
                      <TableCell sx={{ pl: 3, fontStyle: 'italic' }}>{t.currentYearIncome}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold', color: balanceData.currentYearNetIncome >= 0 ? 'success.main' : 'error.main' }}>
                        {formatNumber(balanceData.currentYearNetIncome)}
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow sx={{ bgcolor: isDark ? '#1b4332' : '#81c784' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t.totalEquity}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{formatNumber(balanceData.totalEquity)}</TableCell>
                  </TableRow>

                  {/* Total Liabilities & Equity */}
                  <TableRow sx={{ bgcolor: '#ff6f00' }}>
                    <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>{t.totalLiabAndEquity}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold', fontSize: '1.1rem', color: 'white' }}>
                      {formatNumber(balanceData.totalLiabilitiesAndEquity)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </Fade>
    );
  };

  // ================== CASH FLOW TAB ==================
  const renderCashFlow = () => {
    if (!cashFlowData) return <Box sx={{ textAlign: 'center', p: 5 }}><SwapVert sx={{ fontSize: 60, color: 'action.disabled', mb: 2 }} /><Typography color="textSecondary">{t.noData}</Typography></Box>;

    return (
      <Fade in>
        <Box>
          {/* Summary Cards */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            <Card sx={{ flex: '1 1 200px' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <AttachMoney sx={{ fontSize: 32, color: 'info.main' }} />
                <Typography variant="caption" color="textSecondary">{t.beginningCash}</Typography>
                <Typography variant="h5" fontWeight="bold" color="info.main">{formatNumber(cashFlowData.beginningCashBalance)}</Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: '1 1 200px', bgcolor: cashFlowData.netChangeInCash >= 0 ? (isDark ? '#1b4332' : '#e8f5e9') : (isDark ? '#4a1515' : '#ffebee') }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <SwapVert sx={{ fontSize: 32, color: cashFlowData.netChangeInCash >= 0 ? 'success.main' : 'error.main' }} />
                <Typography variant="caption" color="textSecondary">{t.netChangeInCash}</Typography>
                <Typography variant="h5" fontWeight="bold" color={cashFlowData.netChangeInCash >= 0 ? 'success.main' : 'error.main'}>
                  {formatNumber(cashFlowData.netChangeInCash)}
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: '1 1 200px', border: '2px solid', borderColor: 'primary.main' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Savings sx={{ fontSize: 32, color: 'primary.main' }} />
                <Typography variant="caption" color="textSecondary">{t.endingCash}</Typography>
                <Typography variant="h4" fontWeight="bold" color="primary.main">{formatNumber(cashFlowData.endingCashBalance)}</Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Cash Flow Table */}
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}></TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold', width: 150 }}>{t.amount}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Operating Activities */}
                <TableRow sx={{ bgcolor: isDark ? '#1565c0' : '#bbdefb' }}>
                  <TableCell colSpan={2} sx={{ fontWeight: 'bold', fontSize: '1rem' }}>{t.operatingActivities}</TableCell>
                </TableRow>
                {cashFlowData.operatingActivities.map((item, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell sx={{ pl: 3 }}>{isRTL ? item.descriptionAr : item.description}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', color: item.amount >= 0 ? 'success.main' : 'error.main' }}>
                      {formatNumber(item.amount)}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor: isDark ? '#0d47a1' : '#90caf9' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>{t.netCashOperating}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{formatNumber(cashFlowData.netCashFromOperating)}</TableCell>
                </TableRow>

                {/* Investing Activities */}
                {cashFlowData.investingActivities.length > 0 && (
                  <>
                    <TableRow sx={{ bgcolor: isDark ? '#7b1fa2' : '#e1bee7' }}>
                      <TableCell colSpan={2} sx={{ fontWeight: 'bold', fontSize: '1rem' }}>{t.investingActivities}</TableCell>
                    </TableRow>
                    {cashFlowData.investingActivities.map((item, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell sx={{ pl: 3 }}>{isRTL ? item.descriptionAr : item.description}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', color: item.amount >= 0 ? 'success.main' : 'error.main' }}>
                          {formatNumber(item.amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: isDark ? '#6a1b9a' : '#ce93d8' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>{t.netCashInvesting}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{formatNumber(cashFlowData.netCashFromInvesting)}</TableCell>
                    </TableRow>
                  </>
                )}

                {/* Financing Activities */}
                {cashFlowData.financingActivities.length > 0 && (
                  <>
                    <TableRow sx={{ bgcolor: isDark ? '#ef6c00' : '#ffe0b2' }}>
                      <TableCell colSpan={2} sx={{ fontWeight: 'bold', fontSize: '1rem' }}>{t.financingActivities}</TableCell>
                    </TableRow>
                    {cashFlowData.financingActivities.map((item, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell sx={{ pl: 3 }}>{isRTL ? item.descriptionAr : item.description}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', color: item.amount >= 0 ? 'success.main' : 'error.main' }}>
                          {formatNumber(item.amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: isDark ? '#e65100' : '#ffcc80' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>{t.netCashFinancing}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{formatNumber(cashFlowData.netCashFromFinancing)}</TableCell>
                    </TableRow>
                  </>
                )}

                {/* Net Change Summary */}
                <TableRow sx={{ bgcolor: '#ff6f00' }}>
                  <TableCell sx={{ fontWeight: 'bold', color: 'white', fontSize: '1.1rem' }}>{t.netChangeInCash}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold', fontSize: '1.2rem', color: 'white' }}>
                    {formatNumber(cashFlowData.netChangeInCash)}
                  </TableCell>
                </TableRow>

                {/* Beginning & Ending Cash */}
                <TableRow>
                  <TableCell>{t.beginningCash}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{formatNumber(cashFlowData.beginningCashBalance)}</TableCell>
                </TableRow>
                <TableRow sx={{ bgcolor: isDark ? '#1b4332' : '#c8e6c9' }}>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>{t.endingCash}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold', fontSize: '1.2rem' }}>{formatNumber(cashFlowData.endingCashBalance)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Fade>
    );
  };

  // ================== EXPORT FUNCTIONS ==================
  const exportToExcel = async () => {
    const wb = new ExcelJS.Workbook();
    wb.creator = companyInfo?.companyName || 'Countora';
    wb.created = new Date();
    
    // ==================== INCOME STATEMENT ====================
    if (incomeData && exportOptions.incomeStatement) {
      const ws = wb.addWorksheet(t.incomeStatement, { views: [{ showGridLines: false }] });
      
      // Logo & Header
      if (companyInfo?.logoBase64) {
        try {
          const img = wb.addImage({ base64: companyInfo.logoBase64, extension: 'png' });
          ws.addImage(img, { tl: { col: 0, row: 0 }, ext: { width: 80, height: 80 } });
        } catch (e) {}
      }
      
      // Company Name
      ws.mergeCells('B1:D1');
      const titleCell = ws.getCell('B1');
      titleCell.value = companyInfo?.companyName || '';
      titleCell.font = { size: 18, bold: true, color: { argb: 'FF1976D2' } };
      titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
      
      // Report Title
      ws.mergeCells('B2:D2');
      const reportCell = ws.getCell('B2');
      reportCell.value = t.incomeStatement;
      reportCell.font = { size: 14, bold: true };
      reportCell.alignment = { horizontal: 'center' };
      
      // Period
      ws.mergeCells('B3:D3');
      const periodCell = ws.getCell('B3');
      periodCell.value = `${t.period}: ${formatDate(fromDate)} - ${formatDate(toDate)}`;
      periodCell.font = { size: 10, italic: true };
      periodCell.alignment = { horizontal: 'center' };
      
      let row = 5;
      
      // Revenue Section
      ws.mergeCells(`A${row}:D${row}`);
      const revHeader = ws.getCell(`A${row}`);
      revHeader.value = t.revenue;
      revHeader.font = { size: 12, bold: true, color: { argb: 'FFFFFFFF' } };
      revHeader.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4CAF50' } };
      revHeader.alignment = { horizontal: 'center', vertical: 'middle' };
      revHeader.border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };
      row++;
      
      incomeData.revenueLines.forEach(line => {
        ws.getCell(`A${row}`).value = line.accountCode;
        ws.getCell(`B${row}`).value = isRTL ? line.accountNameAr : line.accountName;
        ws.getCell(`C${row}`).value = line.currentPeriodAmount;
        ws.getCell(`C${row}`).numFmt = '#,##0.000';
        ws.getCell(`C${row}`).font = { color: { argb: 'FF4CAF50' } };
        ['A', 'B', 'C'].forEach(col => {
          ws.getCell(`${col}${row}`).border = { left: { style: 'thin' }, right: { style: 'thin' } };
        });
        row++;
      });
      
      // Total Revenue
      ws.getCell(`A${row}`).value = t.totalRevenue;
      ws.getCell(`A${row}`).font = { bold: true };
      ws.getCell(`C${row}`).value = incomeData.totalRevenue;
      ws.getCell(`C${row}`).numFmt = '#,##0.000';
      ws.getCell(`C${row}`).font = { bold: true, color: { argb: 'FF4CAF50' } };
      ws.getCell(`C${row}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8F5E9' } };
      ['A', 'B', 'C'].forEach(col => {
        ws.getCell(`${col}${row}`).border = { top: { style: 'medium' }, bottom: { style: 'medium' }, left: { style: 'thin' }, right: { style: 'thin' } };
      });
      row += 2;
      
      // Expenses Section
      ws.mergeCells(`A${row}:D${row}`);
      const expHeader = ws.getCell(`A${row}`);
      expHeader.value = t.expenses;
      expHeader.font = { size: 12, bold: true, color: { argb: 'FFFFFFFF' } };
      expHeader.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF44336' } };
      expHeader.alignment = { horizontal: 'center', vertical: 'middle' };
      expHeader.border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };
      row++;
      
      incomeData.expenseLines.forEach(line => {
        ws.getCell(`A${row}`).value = line.accountCode;
        ws.getCell(`B${row}`).value = isRTL ? line.accountNameAr : line.accountName;
        ws.getCell(`C${row}`).value = line.currentPeriodAmount;
        ws.getCell(`C${row}`).numFmt = '#,##0.000';
        ws.getCell(`C${row}`).font = { color: { argb: 'FFF44336' } };
        ['A', 'B', 'C'].forEach(col => {
          ws.getCell(`${col}${row}`).border = { left: { style: 'thin' }, right: { style: 'thin' } };
        });
        row++;
      });
      
      // Total Expenses
      ws.getCell(`A${row}`).value = t.totalExpenses;
      ws.getCell(`A${row}`).font = { bold: true };
      ws.getCell(`C${row}`).value = incomeData.totalExpenses;
      ws.getCell(`C${row}`).numFmt = '#,##0.000';
      ws.getCell(`C${row}`).font = { bold: true, color: { argb: 'FFF44336' } };
      ws.getCell(`C${row}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFEBEE' } };
      ['A', 'B', 'C'].forEach(col => {
        ws.getCell(`${col}${row}`).border = { top: { style: 'medium' }, bottom: { style: 'medium' }, left: { style: 'thin' }, right: { style: 'thin' } };
      });
      row += 2;
      
      // Net Income
      const isProfit = incomeData.netIncome >= 0;
      ws.mergeCells(`A${row}:B${row}`);
      ws.getCell(`A${row}`).value = isProfit ? t.netIncome : t.netLoss;
      ws.getCell(`A${row}`).font = { size: 14, bold: true, color: { argb: 'FFFFFFFF' } };
      ws.getCell(`A${row}`).alignment = { horizontal: 'center' };
      ws.getCell(`C${row}`).value = Math.abs(incomeData.netIncome);
      ws.getCell(`C${row}`).numFmt = '#,##0.000';
      ws.getCell(`C${row}`).font = { size: 14, bold: true, color: { argb: 'FFFFFFFF' } };
      ['A', 'C'].forEach(col => {
        ws.getCell(`${col}${row}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: isProfit ? 'FF4CAF50' : 'FFF44336' } };
        ws.getCell(`${col}${row}`).border = { top: { style: 'double' }, bottom: { style: 'double' }, left: { style: 'medium' }, right: { style: 'medium' } };
      });
      
      ws.columns = [{ width: 15 }, { width: 40 }, { width: 20 }, { width: 15 }];
    }

    // ==================== BALANCE SHEET ====================
    if (balanceData && exportOptions.balanceSheet) {
      const ws = wb.addWorksheet(t.balanceSheet, { views: [{ showGridLines: false }] });
      
      // Logo & Header
      if (companyInfo?.logoBase64) {
        try {
          const img = wb.addImage({ base64: companyInfo.logoBase64, extension: 'png' });
          ws.addImage(img, { tl: { col: 0, row: 0 }, ext: { width: 80, height: 80 } });
        } catch (e) {}
      }
      
      ws.mergeCells('B1:D1');
      ws.getCell('B1').value = companyInfo?.companyName || '';
      ws.getCell('B1').font = { size: 18, bold: true, color: { argb: 'FF1976D2' } };
      ws.getCell('B1').alignment = { horizontal: 'center', vertical: 'middle' };
      
      ws.mergeCells('B2:D2');
      ws.getCell('B2').value = t.balanceSheet;
      ws.getCell('B2').font = { size: 14, bold: true };
      ws.getCell('B2').alignment = { horizontal: 'center' };
      
      ws.mergeCells('B3:D3');
      ws.getCell('B3').value = `${t.period}: ${formatDate(toDate)}`;
      ws.getCell('B3').font = { size: 10, italic: true };
      ws.getCell('B3').alignment = { horizontal: 'center' };
      
      let row = 5;
      
      // Assets
      ws.mergeCells(`A${row}:C${row}`);
      const assetsHeader = ws.getCell(`A${row}`);
      assetsHeader.value = t.assets;
      assetsHeader.font = { size: 12, bold: true, color: { argb: 'FFFFFFFF' } };
      assetsHeader.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1565C0' } };
      assetsHeader.alignment = { horizontal: 'center' };
      assetsHeader.border = { top: { style: 'medium' }, bottom: { style: 'thin' }, left: { style: 'medium' }, right: { style: 'medium' } };
      row++;
      
      // Current Assets
      ws.getCell(`A${row}`).value = t.currentAssets;
      ws.getCell(`A${row}`).font = { bold: true, italic: true };
      ws.getCell(`A${row}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE3F2FD' } };
      row++;
      
      balanceData.currentAssets.forEach(item => {
        ws.getCell(`A${row}`).value = item.accountCode;
        ws.getCell(`B${row}`).value = isRTL ? item.accountNameAr : item.accountName;
        ws.getCell(`C${row}`).value = item.currentPeriodBalance;
        ws.getCell(`C${row}`).numFmt = '#,##0.000';
        row++;
      });
      
      ws.getCell(`B${row}`).value = t.totalCurrentAssets;
      ws.getCell(`B${row}`).font = { bold: true };
      ws.getCell(`C${row}`).value = balanceData.totalCurrentAssets;
      ws.getCell(`C${row}`).numFmt = '#,##0.000';
      ws.getCell(`C${row}`).font = { bold: true };
      ws.getCell(`C${row}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFBBDEFB' } };
      ['B', 'C'].forEach(col => ws.getCell(`${col}${row}`).border = { top: { style: 'thin' }, bottom: { style: 'thin' } });
      row += 2;
      
      // Fixed Assets
      if (balanceData.fixedAssets.length > 0) {
        ws.getCell(`A${row}`).value = t.fixedAssets;
        ws.getCell(`A${row}`).font = { bold: true, italic: true };
        ws.getCell(`A${row}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE3F2FD' } };
        row++;
        
        balanceData.fixedAssets.forEach(item => {
          ws.getCell(`A${row}`).value = item.accountCode;
          ws.getCell(`B${row}`).value = isRTL ? item.accountNameAr : item.accountName;
          ws.getCell(`C${row}`).value = item.currentPeriodBalance;
          ws.getCell(`C${row}`).numFmt = '#,##0.000';
          row++;
        });
        
        ws.getCell(`B${row}`).value = t.totalFixedAssets;
        ws.getCell(`B${row}`).font = { bold: true };
        ws.getCell(`C${row}`).value = balanceData.totalFixedAssets;
        ws.getCell(`C${row}`).numFmt = '#,##0.000';
        ws.getCell(`C${row}`).font = { bold: true };
        ws.getCell(`C${row}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFBBDEFB' } };
        ['B', 'C'].forEach(col => ws.getCell(`${col}${row}`).border = { top: { style: 'thin' }, bottom: { style: 'thin' } });
        row += 2;
      }
      
      // Total Assets
      ws.mergeCells(`A${row}:B${row}`);
      ws.getCell(`A${row}`).value = t.totalAssets;
      ws.getCell(`A${row}`).font = { size: 12, bold: true, color: { argb: 'FFFFFFFF' } };
      ws.getCell(`A${row}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1565C0' } };
      ws.getCell(`A${row}`).alignment = { horizontal: 'center' };
      ws.getCell(`C${row}`).value = balanceData.totalAssets;
      ws.getCell(`C${row}`).numFmt = '#,##0.000';
      ws.getCell(`C${row}`).font = { size: 12, bold: true, color: { argb: 'FFFFFFFF' } };
      ws.getCell(`C${row}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1565C0' } };
      ['A', 'C'].forEach(col => ws.getCell(`${col}${row}`).border = { top: { style: 'double' }, bottom: { style: 'double' }, left: { style: 'medium' }, right: { style: 'medium' } });
      row += 2;
      
      // Liabilities
      ws.mergeCells(`A${row}:C${row}`);
      const liabHeader = ws.getCell(`A${row}`);
      liabHeader.value = t.liabilities;
      liabHeader.font = { size: 12, bold: true, color: { argb: 'FFFFFFFF' } };
      liabHeader.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC62828' } };
      liabHeader.alignment = { horizontal: 'center' };
      liabHeader.border = { top: { style: 'medium' }, bottom: { style: 'thin' }, left: { style: 'medium' }, right: { style: 'medium' } };
      row++;
      
      balanceData.currentLiabilities.forEach(item => {
        ws.getCell(`A${row}`).value = item.accountCode;
        ws.getCell(`B${row}`).value = isRTL ? item.accountNameAr : item.accountName;
        ws.getCell(`C${row}`).value = item.currentPeriodBalance;
        ws.getCell(`C${row}`).numFmt = '#,##0.000';
        row++;
      });
      
      ws.getCell(`B${row}`).value = t.totalLiabilities;
      ws.getCell(`B${row}`).font = { bold: true };
      ws.getCell(`C${row}`).value = balanceData.totalLiabilities;
      ws.getCell(`C${row}`).numFmt = '#,##0.000';
      ws.getCell(`C${row}`).font = { bold: true };
      ws.getCell(`C${row}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFEBEE' } };
      ['B', 'C'].forEach(col => ws.getCell(`${col}${row}`).border = { top: { style: 'medium' }, bottom: { style: 'medium' } });
      row += 2;
      
      // Equity
      ws.mergeCells(`A${row}:C${row}`);
      const equityHeader = ws.getCell(`A${row}`);
      equityHeader.value = t.equity;
      equityHeader.font = { size: 12, bold: true, color: { argb: 'FFFFFFFF' } };
      equityHeader.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2E7D32' } };
      equityHeader.alignment = { horizontal: 'center' };
      equityHeader.border = { top: { style: 'medium' }, bottom: { style: 'thin' }, left: { style: 'medium' }, right: { style: 'medium' } };
      row++;
      
      balanceData.equityItems.forEach(item => {
        ws.getCell(`A${row}`).value = item.accountCode;
        ws.getCell(`B${row}`).value = isRTL ? item.accountNameAr : item.accountName;
        ws.getCell(`C${row}`).value = item.currentPeriodBalance;
        ws.getCell(`C${row}`).numFmt = '#,##0.000';
        row++;
      });
      
      ws.getCell(`B${row}`).value = t.currentYearIncome;
      ws.getCell(`B${row}`).font = { italic: true };
      ws.getCell(`C${row}`).value = balanceData.currentYearNetIncome;
      ws.getCell(`C${row}`).numFmt = '#,##0.000';
      ws.getCell(`C${row}`).font = { italic: true, color: { argb: balanceData.currentYearNetIncome >= 0 ? 'FF4CAF50' : 'FFF44336' } };
      row++;
      
      ws.getCell(`B${row}`).value = t.totalEquity;
      ws.getCell(`B${row}`).font = { bold: true };
      ws.getCell(`C${row}`).value = balanceData.totalEquity;
      ws.getCell(`C${row}`).numFmt = '#,##0.000';
      ws.getCell(`C${row}`).font = { bold: true };
      ws.getCell(`C${row}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC8E6C9' } };
      ['B', 'C'].forEach(col => ws.getCell(`${col}${row}`).border = { top: { style: 'double' }, bottom: { style: 'double' } });
      
      ws.columns = [{ width: 15 }, { width: 40 }, { width: 20 }];
    }

    // ==================== CASH FLOW ====================
    if (cashFlowData && exportOptions.cashFlow) {
      const ws = wb.addWorksheet(t.cashFlow, { views: [{ showGridLines: false }] });
      
      if (companyInfo?.logoBase64) {
        try {
          const img = wb.addImage({ base64: companyInfo.logoBase64, extension: 'png' });
          ws.addImage(img, { tl: { col: 0, row: 0 }, ext: { width: 80, height: 80 } });
        } catch (e) {}
      }
      
      ws.mergeCells('B1:C1');
      ws.getCell('B1').value = companyInfo?.companyName || '';
      ws.getCell('B1').font = { size: 18, bold: true, color: { argb: 'FF1976D2' } };
      ws.getCell('B1').alignment = { horizontal: 'center', vertical: 'middle' };
      
      ws.mergeCells('B2:C2');
      ws.getCell('B2').value = t.cashFlow;
      ws.getCell('B2').font = { size: 14, bold: true };
      ws.getCell('B2').alignment = { horizontal: 'center' };
      
      ws.mergeCells('B3:C3');
      ws.getCell('B3').value = `${t.period}: ${formatDate(fromDate)} - ${formatDate(toDate)}`;
      ws.getCell('B3').font = { size: 10, italic: true };
      ws.getCell('B3').alignment = { horizontal: 'center' };
      
      let row = 5;
      
      // Operating
      ws.mergeCells(`A${row}:C${row}`);
      ws.getCell(`A${row}`).value = t.operatingActivities;
      ws.getCell(`A${row}`).font = { size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
      ws.getCell(`A${row}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1565C0' } };
      ws.getCell(`A${row}`).alignment = { horizontal: 'center' };
      row++;
      
      cashFlowData.operatingActivities.forEach(item => {
        ws.getCell(`A${row}`).value = isRTL ? item.descriptionAr : item.description;
        ws.getCell(`B${row}`).value = item.amount;
        ws.getCell(`B${row}`).numFmt = '#,##0.000';
        ws.getCell(`B${row}`).font = { color: { argb: item.amount >= 0 ? 'FF4CAF50' : 'FFF44336' } };
        row++;
      });
      
      ws.getCell(`A${row}`).value = t.netCashOperating;
      ws.getCell(`A${row}`).font = { bold: true };
      ws.getCell(`B${row}`).value = cashFlowData.netCashFromOperating;
      ws.getCell(`B${row}`).numFmt = '#,##0.000';
      ws.getCell(`B${row}`).font = { bold: true };
      ws.getCell(`B${row}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE3F2FD' } };
      ws.getCell(`B${row}`).border = { top: { style: 'medium' }, bottom: { style: 'medium' } };
      row += 2;
      
      // Investing
      if (cashFlowData.investingActivities.length > 0) {
        ws.mergeCells(`A${row}:C${row}`);
        ws.getCell(`A${row}`).value = t.investingActivities;
        ws.getCell(`A${row}`).font = { size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
        ws.getCell(`A${row}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF7B1FA2' } };
        ws.getCell(`A${row}`).alignment = { horizontal: 'center' };
        row++;
        
        cashFlowData.investingActivities.forEach(item => {
          ws.getCell(`A${row}`).value = isRTL ? item.descriptionAr : item.description;
          ws.getCell(`B${row}`).value = item.amount;
          ws.getCell(`B${row}`).numFmt = '#,##0.000';
          ws.getCell(`B${row}`).font = { color: { argb: item.amount >= 0 ? 'FF4CAF50' : 'FFF44336' } };
          row++;
        });
        
        ws.getCell(`A${row}`).value = t.netCashInvesting;
        ws.getCell(`A${row}`).font = { bold: true };
        ws.getCell(`B${row}`).value = cashFlowData.netCashFromInvesting;
        ws.getCell(`B${row}`).numFmt = '#,##0.000';
        ws.getCell(`B${row}`).font = { bold: true };
        ws.getCell(`B${row}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE1BEE7' } };
        ws.getCell(`B${row}`).border = { top: { style: 'medium' }, bottom: { style: 'medium' } };
        row += 2;
      }
      
      // Financing
      if (cashFlowData.financingActivities.length > 0) {
        ws.mergeCells(`A${row}:C${row}`);
        ws.getCell(`A${row}`).value = t.financingActivities;
        ws.getCell(`A${row}`).font = { size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
        ws.getCell(`A${row}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEF6C00' } };
        ws.getCell(`A${row}`).alignment = { horizontal: 'center' };
        row++;
        
        cashFlowData.financingActivities.forEach(item => {
          ws.getCell(`A${row}`).value = isRTL ? item.descriptionAr : item.description;
          ws.getCell(`B${row}`).value = item.amount;
          ws.getCell(`B${row}`).numFmt = '#,##0.000';
          ws.getCell(`B${row}`).font = { color: { argb: item.amount >= 0 ? 'FF4CAF50' : 'FFF44336' } };
          row++;
        });
        
        ws.getCell(`A${row}`).value = t.netCashFinancing;
        ws.getCell(`A${row}`).font = { bold: true };
        ws.getCell(`B${row}`).value = cashFlowData.netCashFromFinancing;
        ws.getCell(`B${row}`).numFmt = '#,##0.000';
        ws.getCell(`B${row}`).font = { bold: true };
        ws.getCell(`B${row}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFE0B2' } };
        ws.getCell(`B${row}`).border = { top: { style: 'medium' }, bottom: { style: 'medium' } };
        row += 2;
      }
      
      // Net Change
      ws.mergeCells(`A${row}:A${row}`);
      ws.getCell(`A${row}`).value = t.netChangeInCash;
      ws.getCell(`A${row}`).font = { size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
      ws.getCell(`A${row}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFF6F00' } };
      ws.getCell(`B${row}`).value = cashFlowData.netChangeInCash;
      ws.getCell(`B${row}`).numFmt = '#,##0.000';
      ws.getCell(`B${row}`).font = { size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
      ws.getCell(`B${row}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFF6F00' } };
      ws.getCell(`B${row}`).border = { top: { style: 'double' }, bottom: { style: 'double' } };
      row += 2;
      
      ws.getCell(`A${row}`).value = t.beginningCash;
      ws.getCell(`B${row}`).value = cashFlowData.beginningCashBalance;
      ws.getCell(`B${row}`).numFmt = '#,##0.000';
      row++;
      
      ws.getCell(`A${row}`).value = t.endingCash;
      ws.getCell(`A${row}`).font = { bold: true };
      ws.getCell(`B${row}`).value = cashFlowData.endingCashBalance;
      ws.getCell(`B${row}`).numFmt = '#,##0.000';
      ws.getCell(`B${row}`).font = { bold: true };
      ws.getCell(`B${row}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC8E6C9' } };
      ws.getCell(`B${row}`).border = { top: { style: 'double' }, bottom: { style: 'double' } };
      
      ws.columns = [{ width: 45 }, { width: 20 }, { width: 15 }];
    }

    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `FinancialStatements_${new Date().toISOString().split('T')[0]}.xlsx`; a.click();
    toast.success(isRTL ? 'تم التصدير لإكسل' : 'Excel exported');
  };

  const exportToPDF = () => {
    if (!exportOptions.incomeStatement && !exportOptions.balanceSheet && !exportOptions.cashFlow) {
      toast.warning(isRTL ? 'يرجى اختيار قائمة واحدة على الأقل' : 'Please select at least one statement');
      return;
    }

    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pw = doc.internal.pageSize.getWidth();
    let yPos = 28;
    let isFirstPage = true;

    const addHeader = (yStart: number = 5, addPeriod: boolean = false) => {
      if (companyInfo?.logoBase64) { try { doc.addImage(companyInfo.logoBase64, 'PNG', 10, yStart, 20, 20); } catch (e) {} }
      doc.setFontSize(16); 
      doc.setTextColor(25, 118, 210);
      doc.setFont('helvetica', 'bold');
      doc.text(companyInfo?.companyName || '', pw / 2, yStart + 7, { align: 'center' });
      
      if (addPeriod) {
        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);
        doc.setFont('helvetica', 'italic');
        doc.text(`${t.period}: ${formatDate(fromDate)} - ${formatDate(toDate)}`, pw / 2, yStart + 13, { align: 'center' });
      }
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');
    };

    // Income Statement
    if (incomeData && exportOptions.incomeStatement) {
      if (!isFirstPage) { doc.addPage(); yPos = 28; } else { isFirstPage = false; }
      addHeader(5, true);
      
      doc.setFontSize(13); 
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(76, 175, 80);
      doc.text(t.incomeStatement, pw / 2, 24, { align: 'center' });
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');
      
      const body: any[] = [
        [{ content: t.revenue, colSpan: 3, styles: { fillColor: [76, 175, 80] as [number, number, number], textColor: [255, 255, 255] as [number, number, number], fontStyle: 'bold' as const, halign: 'center' as const } }],
        ...incomeData.revenueLines.map(l => [l.accountCode, isRTL ? l.accountNameAr : l.accountName, { content: formatNumber(l.currentPeriodAmount), styles: { textColor: [76, 175, 80] as [number, number, number] } }]),
        [{ content: t.totalRevenue, colSpan: 2, styles: { fontStyle: 'bold' as const, fillColor: [232, 245, 233] as [number, number, number] } }, { content: formatNumber(incomeData.totalRevenue), styles: { fontStyle: 'bold' as const, fillColor: [232, 245, 233] as [number, number, number], textColor: [56, 142, 60] as [number, number, number] } }],
        [{ content: t.expenses, colSpan: 3, styles: { fillColor: [244, 67, 54] as [number, number, number], textColor: [255, 255, 255] as [number, number, number], fontStyle: 'bold' as const, halign: 'center' as const } }],
        ...incomeData.expenseLines.map(l => [l.accountCode, isRTL ? l.accountNameAr : l.accountName, { content: formatNumber(l.currentPeriodAmount), styles: { textColor: [211, 47, 47] as [number, number, number] } }]),
        [{ content: t.totalExpenses, colSpan: 2, styles: { fontStyle: 'bold' as const, fillColor: [255, 235, 238] as [number, number, number] } }, { content: formatNumber(incomeData.totalExpenses), styles: { fontStyle: 'bold' as const, fillColor: [255, 235, 238] as [number, number, number], textColor: [198, 40, 40] as [number, number, number] } }],
        [{ content: incomeData.netIncome >= 0 ? t.netIncome : t.netLoss, colSpan: 2, styles: { fontStyle: 'bold' as const, fillColor: (incomeData.netIncome >= 0 ? [76, 175, 80] : [244, 67, 54]) as [number, number, number], textColor: [255, 255, 255] as [number, number, number], fontSize: 11 } }, 
         { content: formatNumber(Math.abs(incomeData.netIncome)), styles: { fontStyle: 'bold' as const, fillColor: (incomeData.netIncome >= 0 ? [76, 175, 80] : [244, 67, 54]) as [number, number, number], textColor: [255, 255, 255] as [number, number, number], fontSize: 11 } }],
      ];
      autoTable(doc, { 
        head: [[t.accountCode, t.accountName, t.amount]], 
        body, 
        startY: yPos, 
        styles: { fontSize: 9, cellPadding: 2 }, 
        headStyles: { fillColor: [25, 118, 210], fontSize: 10 },
        columnStyles: { 0: { cellWidth: 25 }, 1: { cellWidth: 'auto' }, 2: { cellWidth: 35, halign: 'right' as const } }
      });
      yPos = (doc as any).lastAutoTable.finalY + 10;
    }

    // Balance Sheet
    if (balanceData && exportOptions.balanceSheet) {
      if (!isFirstPage) { doc.addPage(); yPos = 28; } else { isFirstPage = false; }
      if (incomeData && exportOptions.incomeStatement) { doc.addPage(); yPos = 28; }
      addHeader(5, true);
      
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(33, 150, 243);
      doc.text(t.balanceSheet, pw / 2, 24, { align: 'center' });
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');
      
      const balanceBody: any[] = [
        [{ content: t.assets, colSpan: 2, styles: { fontStyle: 'bold' as const, fillColor: [21, 101, 192] as [number, number, number], textColor: [255, 255, 255] as [number, number, number], halign: 'center' as const } }],
        [{ content: t.currentAssets, colSpan: 2, styles: { fontStyle: 'italic' as const, fillColor: [227, 242, 253] as [number, number, number] } }],
        ...balanceData.currentAssets.map(a => [isRTL ? a.accountNameAr : a.accountName, formatNumber(a.currentPeriodBalance)]),
        [{ content: t.totalCurrentAssets, styles: { fontStyle: 'bold' as const, fillColor: [187, 222, 251] as [number, number, number] } }, { content: formatNumber(balanceData.totalCurrentAssets), styles: { fontStyle: 'bold' as const, fillColor: [187, 222, 251] as [number, number, number] } }],
      ];
      
      if (balanceData.fixedAssets.length > 0) {
        balanceBody.push([{ content: t.fixedAssets, colSpan: 2, styles: { fontStyle: 'italic' as const, fillColor: [227, 242, 253] as [number, number, number] } }]);
        balanceData.fixedAssets.forEach(a => balanceBody.push([isRTL ? a.accountNameAr : a.accountName, formatNumber(a.currentPeriodBalance)]));
        balanceBody.push([{ content: t.totalFixedAssets, styles: { fontStyle: 'bold' as const, fillColor: [187, 222, 251] as [number, number, number] } }, { content: formatNumber(balanceData.totalFixedAssets), styles: { fontStyle: 'bold' as const, fillColor: [187, 222, 251] as [number, number, number] } }]);
      }
      
      balanceBody.push(
        [{ content: t.totalAssets, styles: { fontStyle: 'bold' as const, fillColor: [21, 101, 192] as [number, number, number], textColor: [255, 255, 255] as [number, number, number], fontSize: 10 } }, { content: formatNumber(balanceData.totalAssets), styles: { fontStyle: 'bold' as const, fillColor: [21, 101, 192] as [number, number, number], textColor: [255, 255, 255] as [number, number, number], fontSize: 10 } }],
        [{ content: t.liabilities, colSpan: 2, styles: { fontStyle: 'bold' as const, fillColor: [198, 40, 40] as [number, number, number], textColor: [255, 255, 255] as [number, number, number], halign: 'center' as const } }],
        ...balanceData.currentLiabilities.map(l => [isRTL ? l.accountNameAr : l.accountName, formatNumber(l.currentPeriodBalance)]),
        [{ content: t.totalLiabilities, styles: { fontStyle: 'bold' as const, fillColor: [255, 235, 238] as [number, number, number] } }, { content: formatNumber(balanceData.totalLiabilities), styles: { fontStyle: 'bold' as const, fillColor: [255, 235, 238] as [number, number, number] } }],
        [{ content: t.equity, colSpan: 2, styles: { fontStyle: 'bold' as const, fillColor: [46, 125, 50] as [number, number, number], textColor: [255, 255, 255] as [number, number, number], halign: 'center' as const } }],
        ...balanceData.equityItems.map(e => [isRTL ? e.accountNameAr : e.accountName, formatNumber(e.currentPeriodBalance)]),
        [{ content: t.currentYearIncome, styles: { fontStyle: 'italic' as const } }, { content: formatNumber(balanceData.currentYearNetIncome), styles: { fontStyle: 'italic' as const, textColor: (balanceData.currentYearNetIncome >= 0 ? [76, 175, 80] : [244, 67, 54]) as [number, number, number] } }],
        [{ content: t.totalEquity, styles: { fontStyle: 'bold' as const, fillColor: [200, 230, 201] as [number, number, number] } }, { content: formatNumber(balanceData.totalEquity), styles: { fontStyle: 'bold' as const, fillColor: [200, 230, 201] as [number, number, number] } }]
      );
      
      autoTable(doc, { 
        head: [[t.accountName, t.balance]], 
        body: balanceBody, 
        startY: yPos, 
        styles: { fontSize: 9, cellPadding: 2 }, 
        headStyles: { fillColor: [25, 118, 210], fontSize: 10 },
        columnStyles: { 0: { cellWidth: 'auto' }, 1: { cellWidth: 40, halign: 'right' as const } }
      });
      yPos = (doc as any).lastAutoTable.finalY + 10;
    }

    // Cash Flow
    if (cashFlowData && exportOptions.cashFlow) {
      if (!isFirstPage) { doc.addPage(); yPos = 28; } else { isFirstPage = false; }
      if ((incomeData && exportOptions.incomeStatement) || (balanceData && exportOptions.balanceSheet)) { doc.addPage(); yPos = 28; }
      addHeader(5, true);
      
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 152, 0);
      doc.text(t.cashFlow, pw / 2, 24, { align: 'center' });
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');
      
      const cashBody: any[] = [
        [{ content: t.operatingActivities, colSpan: 2, styles: { fontStyle: 'bold' as const, fillColor: [21, 101, 192] as [number, number, number], textColor: [255, 255, 255] as [number, number, number], halign: 'center' as const } }],
        ...cashFlowData.operatingActivities.map(a => [isRTL ? a.descriptionAr : a.description, { content: formatNumber(a.amount), styles: { textColor: (a.amount >= 0 ? [76, 175, 80] : [244, 67, 54]) as [number, number, number] } }]),
        [{ content: t.netCashOperating, styles: { fontStyle: 'bold' as const, fillColor: [227, 242, 253] as [number, number, number] } }, { content: formatNumber(cashFlowData.netCashFromOperating), styles: { fontStyle: 'bold' as const, fillColor: [227, 242, 253] as [number, number, number] } }],
      ];
      
      if (cashFlowData.investingActivities.length > 0) {
        cashBody.push([{ content: t.investingActivities, colSpan: 2, styles: { fontStyle: 'bold' as const, fillColor: [123, 31, 162] as [number, number, number], textColor: [255, 255, 255] as [number, number, number], halign: 'center' as const } }]);
        cashFlowData.investingActivities.forEach(a => cashBody.push([isRTL ? a.descriptionAr : a.description, { content: formatNumber(a.amount), styles: { textColor: (a.amount >= 0 ? [76, 175, 80] : [244, 67, 54]) as [number, number, number] } }]));
        cashBody.push([{ content: t.netCashInvesting, styles: { fontStyle: 'bold' as const, fillColor: [225, 190, 231] as [number, number, number] } }, { content: formatNumber(cashFlowData.netCashFromInvesting), styles: { fontStyle: 'bold' as const, fillColor: [225, 190, 231] as [number, number, number] } }]);
      }
      
      if (cashFlowData.financingActivities.length > 0) {
        cashBody.push([{ content: t.financingActivities, colSpan: 2, styles: { fontStyle: 'bold' as const, fillColor: [239, 108, 0] as [number, number, number], textColor: [255, 255, 255] as [number, number, number], halign: 'center' as const } }]);
        cashFlowData.financingActivities.forEach(a => cashBody.push([isRTL ? a.descriptionAr : a.description, { content: formatNumber(a.amount), styles: { textColor: (a.amount >= 0 ? [76, 175, 80] : [244, 67, 54]) as [number, number, number] } }]));
        cashBody.push([{ content: t.netCashFinancing, styles: { fontStyle: 'bold' as const, fillColor: [255, 224, 178] as [number, number, number] } }, { content: formatNumber(cashFlowData.netCashFromFinancing), styles: { fontStyle: 'bold' as const, fillColor: [255, 224, 178] as [number, number, number] } }]);
      }
      
      cashBody.push(
        [{ content: t.netChangeInCash, styles: { fontStyle: 'bold' as const, fillColor: [255, 111, 0] as [number, number, number], textColor: [255, 255, 255] as [number, number, number], fontSize: 10 } }, { content: formatNumber(cashFlowData.netChangeInCash), styles: { fontStyle: 'bold' as const, fillColor: [255, 111, 0] as [number, number, number], textColor: [255, 255, 255] as [number, number, number], fontSize: 10 } }],
        [t.beginningCash, formatNumber(cashFlowData.beginningCashBalance)],
        [{ content: t.endingCash, styles: { fontStyle: 'bold' as const, fillColor: [200, 230, 201] as [number, number, number] } }, { content: formatNumber(cashFlowData.endingCashBalance), styles: { fontStyle: 'bold' as const, fillColor: [200, 230, 201] as [number, number, number] } }]
      );
      
      autoTable(doc, { 
        head: [['', t.amount]], 
        body: cashBody, 
        startY: yPos, 
        styles: { fontSize: 9, cellPadding: 2 }, 
        headStyles: { fillColor: [25, 118, 210], fontSize: 10 },
        columnStyles: { 0: { cellWidth: 'auto' }, 1: { cellWidth: 40, halign: 'right' as const } }
      });
    }

    doc.save(`FinancialStatements_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success(isRTL ? 'تم التصدير لـ PDF' : 'PDF exported');
  };

  const handlePrint = () => {
    if (!exportOptions.incomeStatement && !exportOptions.balanceSheet && !exportOptions.cashFlow) {
      toast.warning(isRTL ? 'يرجى اختيار قائمة واحدة على الأقل' : 'Please select at least one statement');
      return;
    }

    const content = `<!DOCTYPE html><html dir="${isRTL ? 'rtl' : 'ltr'}"><head><title>${t.title}</title>
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700&display=swap');
        body{font-family: ${isRTL ? 'Cairo' : 'Arial'}, sans-serif;margin:20px;font-size:11px;color:#333;line-height:1.6;}
        .header{text-align:center;margin-bottom:25px;padding:15px;border-bottom:3px solid #1976d2;}
        .header img{max-height:60px;margin-bottom:10px;}
        .header h1{margin:5px 0;color:#1976d2;font-size:22px;}
        .header .period{color:#666;font-size:11px;font-style:italic;margin-top:5px;}
        .report-title{text-align:center;font-size:16px;font-weight:bold;margin:20px 0 15px;padding:8px;border-radius:5px;color:#fff;}
        .report-title.income{background:linear-gradient(135deg, #4caf50 0%, #388e3c 100%);}
        .report-title.balance{background:linear-gradient(135deg, #2196f3 0%, #1976d2 100%);}
        .report-title.cashflow{background:linear-gradient(135deg, #ff9800 0%, #f57c00 100%);}
        table{width:100%;border-collapse:collapse;margin:15px 0;box-shadow:0 2px 8px rgba(0,0,0,0.1);}
        th,td{border:1px solid #ddd;padding:8px;}
        th{background:#1976d2;color:#fff;font-weight:600;font-size:10px;}
        .section{background:#e3f2fd;font-weight:bold;color:#1976d2;}
        .section-revenue{background:linear-gradient(135deg, #4caf50 0%, #388e3c 100%);color:#fff;font-weight:bold;}
        .section-expense{background:linear-gradient(135deg, #f44336 0%, #d32f2f 100%);color:#fff;font-weight:bold;}
        .total{background:#ffc107;font-weight:bold;font-size:12px;}
        .grand-total{background:#1976d2;color:#fff;font-weight:bold;font-size:13px;}
        .profit{background:linear-gradient(135deg, #4caf50 0%, #388e3c 100%);color:#fff;font-weight:bold;font-size:14px;}
        .loss{background:linear-gradient(135deg, #f44336 0%, #d32f2f 100%);color:#fff;font-weight:bold;font-size:14px;}
        .assets-header{background:#1565c0;color:#fff;font-weight:bold;}
        .liabilities-header{background:#c62828;color:#fff;font-weight:bold;}
        .equity-header{background:#2e7d32;color:#fff;font-weight:bold;}
        .text-right{text-align:right;}
        .text-green{color:#4caf50;font-weight:600;}
        .text-red{color:#f44336;font-weight:600;}
        .parent-row{background:#f5f5f5;font-weight:bold;}
        .parent-row td{font-weight:bold;}
        .child-row td:first-child{padding-${isRTL ? 'right' : 'left'}:30px;}
        .page-break{page-break-before:always;margin-top:40px;padding-top:20px;border-top:3px dashed #ccc;}
        @media print{body{margin:10px;}table{box-shadow:none;}.page-break{border-top:none;margin-top:0;padding-top:0;}}
      </style></head>
      <body>
        <div class="header">
          ${companyInfo?.logoPath ? `<img src="${config.API_ORIGIN}${companyInfo.logoPath}" onerror="this.style.display='none'"/>` : ''}
          <h1>${isRTL ? (companyInfo?.companyNameAr || companyInfo?.companyName || '') : (companyInfo?.companyName || '')}</h1>
          <div class="period">${t.period}: ${formatDate(fromDate)} - ${formatDate(toDate)}</div>
        </div>
        
        ${incomeData && exportOptions.incomeStatement ? `
          <div class="report-title income">${t.incomeStatement}</div>
          <table>
            <thead><tr><th>${t.accountCode}</th><th>${t.accountName}</th><th style="text-align:right">${t.amount}</th></tr></thead>
            <tbody>
            ${incomeData.revenueLines && incomeData.revenueLines.length > 0 ? `
              <tr class="section-revenue"><td colspan="3">${t.revenue}</td></tr>
              ${incomeData.revenueLines.map(l => `<tr class="${l.isHeader ? 'parent-row' : 'child-row'}"><td>${l.accountCode}</td><td>${isRTL ? l.accountNameAr : l.accountName}</td><td class="text-right text-green">${formatNumber(l.currentPeriodAmount)}</td></tr>`).join('')}
              <tr class="total"><td colspan="2">${t.totalRevenue}</td><td class="text-right">${formatNumber(incomeData.totalRevenue)}</td></tr>
            ` : ''}
            ${incomeData.cogsLines && incomeData.cogsLines.length > 0 ? `
              <tr class="section-expense"><td colspan="3">${t.cogs}</td></tr>
              ${incomeData.cogsLines.map(l => `<tr class="${l.isHeader ? 'parent-row' : 'child-row'}"><td>${l.accountCode}</td><td>${isRTL ? l.accountNameAr : l.accountName}</td><td class="text-right text-red">${formatNumber(l.currentPeriodAmount)}</td></tr>`).join('')}
              <tr class="total"><td colspan="2">${t.totalCOGS}</td><td class="text-right">${formatNumber(incomeData.totalCOGS)}</td></tr>
            ` : ''}
            ${incomeData.grossProfit !== undefined && incomeData.grossProfit !== 0 ? `
              <tr class="profit"><td colspan="2">${t.grossProfit}</td><td class="text-right">${formatNumber(incomeData.grossProfit)}</td></tr>
            ` : ''}
            ${incomeData.operatingExpenseLines && incomeData.operatingExpenseLines.length > 0 ? `
              <tr class="section-expense"><td colspan="3">${t.operatingExpenses}</td></tr>
              ${incomeData.operatingExpenseLines.map(l => `<tr class="${l.isHeader ? 'parent-row' : 'child-row'}"><td>${l.accountCode}</td><td>${isRTL ? l.accountNameAr : l.accountName}</td><td class="text-right text-red">${formatNumber(l.currentPeriodAmount)}</td></tr>`).join('')}
              <tr class="total"><td colspan="2">${t.totalOperatingExpenses}</td><td class="text-right">${formatNumber(incomeData.totalOperatingExpenses)}</td></tr>
            ` : ''}
            ${incomeData.operatingIncome !== undefined && incomeData.operatingIncome !== 0 ? `
              <tr class="grand-total"><td colspan="2">${t.operatingIncome}</td><td class="text-right">${formatNumber(incomeData.operatingIncome)}</td></tr>
            ` : ''}
            ${incomeData.otherIncomeLines && incomeData.otherIncomeLines.length > 0 ? `
              <tr class="section-revenue"><td colspan="3">${t.otherIncome}</td></tr>
              ${incomeData.otherIncomeLines.map(l => `<tr class="${l.isHeader ? 'parent-row' : 'child-row'}"><td>${l.accountCode}</td><td>${isRTL ? l.accountNameAr : l.accountName}</td><td class="text-right text-green">${formatNumber(l.currentPeriodAmount)}</td></tr>`).join('')}
              <tr class="total"><td colspan="2">${t.totalOtherIncome}</td><td class="text-right">${formatNumber(incomeData.totalOtherIncome)}</td></tr>
            ` : ''}
            ${incomeData.otherExpenseLines && incomeData.otherExpenseLines.length > 0 ? `
              <tr class="section-expense"><td colspan="3">${t.otherExpenses}</td></tr>
              ${incomeData.otherExpenseLines.map(l => `<tr class="${l.isHeader ? 'parent-row' : 'child-row'}"><td>${l.accountCode}</td><td>${isRTL ? l.accountNameAr : l.accountName}</td><td class="text-right text-red">${formatNumber(l.currentPeriodAmount)}</td></tr>`).join('')}
              <tr class="total"><td colspan="2">${t.totalOtherExpenses}</td><td class="text-right">${formatNumber(incomeData.totalOtherExpenses)}</td></tr>
            ` : ''}
            ${incomeData.incomeBeforeTax !== undefined ? `
              <tr class="grand-total"><td colspan="2">${t.incomeBeforeTax}</td><td class="text-right">${formatNumber(incomeData.incomeBeforeTax)}</td></tr>
            ` : ''}
            ${incomeData.taxExpense !== undefined && incomeData.taxExpense !== 0 ? `
              <tr><td style="padding-${isRTL ? 'right' : 'left'}:25px" colspan="2"><em>${t.taxExpense}</em></td><td class="text-right">${formatNumber(incomeData.taxExpense)}</td></tr>
            ` : ''}
            <tr class="${incomeData.netIncome >= 0 ? 'profit' : 'loss'}"><td colspan="2">${incomeData.netIncome >= 0 ? t.netIncome : t.netLoss}</td><td class="text-right">${formatNumber(Math.abs(incomeData.netIncome))}</td></tr>
            </tbody>
          </table>
        ` : ''}
        
        ${balanceData && exportOptions.balanceSheet ? `
          ${incomeData && exportOptions.incomeStatement ? '<div class="page-break"></div>' : ''}
          <div class="report-title balance">${t.balanceSheet}</div>
          <table>
            <tr class="assets-header"><td colspan="2">${t.assets}</td></tr>
            <tr class="section"><td colspan="2">${t.currentAssets}</td></tr>
            ${balanceData.currentAssets.map(a => `<tr class="${a.isHeader ? 'parent-row' : 'child-row'}"><td>${isRTL ? a.accountNameAr : a.accountName}</td><td class="text-right">${formatNumber(a.currentPeriodBalance)}</td></tr>`).join('')}
            <tr class="total"><td>${t.totalAssets} (${t.currentAssets})</td><td class="text-right">${formatNumber(balanceData.totalCurrentAssets)}</td></tr>
            ${balanceData.fixedAssets.length > 0 ? `
              <tr class="section"><td colspan="2">${t.fixedAssets}</td></tr>
              ${balanceData.fixedAssets.map(a => `<tr class="${a.isHeader ? 'parent-row' : 'child-row'}"><td>${isRTL ? a.accountNameAr : a.accountName}</td><td class="text-right">${formatNumber(a.currentPeriodBalance)}</td></tr>`).join('')}
              <tr class="total"><td>${t.totalAssets} (${t.fixedAssets})</td><td class="text-right">${formatNumber(balanceData.totalFixedAssets)}</td></tr>
            ` : ''}
            <tr class="grand-total"><td>${t.totalAssets}</td><td class="text-right">${formatNumber(balanceData.totalAssets)}</td></tr>
            <tr class="liabilities-header"><td colspan="2">${t.liabilities}</td></tr>
            <tr class="section"><td colspan="2">${t.currentLiabilities}</td></tr>
            ${balanceData.currentLiabilities.map(l => `<tr class="${l.isHeader ? 'parent-row' : 'child-row'}"><td>${isRTL ? l.accountNameAr : l.accountName}</td><td class="text-right">${formatNumber(l.currentPeriodBalance)}</td></tr>`).join('')}
            <tr class="total"><td>${t.totalLiabilities} (${t.currentLiabilities})</td><td class="text-right">${formatNumber(balanceData.totalCurrentLiabilities)}</td></tr>
            ${balanceData.longTermLiabilities && balanceData.longTermLiabilities.length > 0 ? `
              <tr class="section"><td colspan="2">${t.longTermLiabilities}</td></tr>
              ${balanceData.longTermLiabilities.map(l => `<tr class="${l.isHeader ? 'parent-row' : 'child-row'}"><td>${isRTL ? l.accountNameAr : l.accountName}</td><td class="text-right">${formatNumber(l.currentPeriodBalance)}</td></tr>`).join('')}
              <tr class="total"><td>${t.totalLiabilities} (${t.longTermLiabilities})</td><td class="text-right">${formatNumber(balanceData.totalLongTermLiabilities)}</td></tr>
            ` : ''}
            <tr class="grand-total"><td>${t.totalLiabilities}</td><td class="text-right">${formatNumber(balanceData.totalLiabilities)}</td></tr>
            <tr class="equity-header"><td colspan="2">${t.equity}</td></tr>
            ${balanceData.equityItems.map(e => `<tr class="${e.isHeader ? 'parent-row' : 'child-row'}"><td>${isRTL ? e.accountNameAr : e.accountName}</td><td class="text-right">${formatNumber(e.currentPeriodBalance)}</td></tr>`).join('')}
            <tr><td style="font-style:italic">${t.currentYearIncome}</td><td class="text-right ${balanceData.currentYearNetIncome >= 0 ? 'text-green' : 'text-red'}" style="font-weight:bold">${formatNumber(balanceData.currentYearNetIncome)}</td></tr>
            <tr class="total"><td>${t.totalEquity}</td><td class="text-right">${formatNumber(balanceData.totalEquity)}</td></tr>
            <tr class="grand-total"><td>${t.totalLiabAndEquity}</td><td class="text-right">${formatNumber(balanceData.totalLiabilitiesAndEquity)}</td></tr>
          </table>
        ` : ''}
        
        ${cashFlowData && exportOptions.cashFlow ? `
          ${(incomeData && exportOptions.incomeStatement) || (balanceData && exportOptions.balanceSheet) ? '<div class="page-break"></div>' : ''}
          <div class="report-title cashflow">${t.cashFlow}</div>
          <table>
            <tr style="background:#1565c0;color:#fff;font-weight:bold"><td colspan="2">${t.operatingActivities}</td></tr>
            ${cashFlowData.operatingActivities.map(a => `<tr><td>${isRTL ? a.descriptionAr : a.description}</td><td class="text-right ${a.amount >= 0 ? 'text-green' : 'text-red'}">${formatNumber(a.amount)}</td></tr>`).join('')}
            <tr class="total"><td>${t.netCashOperating}</td><td class="text-right">${formatNumber(cashFlowData.netCashFromOperating)}</td></tr>
            ${cashFlowData.investingActivities.length > 0 ? `
              <tr style="background:#7b1fa2;color:#fff;font-weight:bold"><td colspan="2">${t.investingActivities}</td></tr>
              ${cashFlowData.investingActivities.map(a => `<tr><td>${isRTL ? a.descriptionAr : a.description}</td><td class="text-right ${a.amount >= 0 ? 'text-green' : 'text-red'}">${formatNumber(a.amount)}</td></tr>`).join('')}
              <tr class="total"><td>${t.netCashInvesting}</td><td class="text-right">${formatNumber(cashFlowData.netCashFromInvesting)}</td></tr>
            ` : ''}
            ${cashFlowData.financingActivities.length > 0 ? `
              <tr style="background:#ef6c00;color:#fff;font-weight:bold"><td colspan="2">${t.financingActivities}</td></tr>
              ${cashFlowData.financingActivities.map(a => `<tr><td>${isRTL ? a.descriptionAr : a.description}</td><td class="text-right ${a.amount >= 0 ? 'text-green' : 'text-red'}">${formatNumber(a.amount)}</td></tr>`).join('')}
              <tr class="total"><td>${t.netCashFinancing}</td><td class="text-right">${formatNumber(cashFlowData.netCashFromFinancing)}</td></tr>
            ` : ''}
            <tr style="background:#ff6f00;color:#fff;font-weight:bold;font-size:12px"><td>${t.netChangeInCash}</td><td class="text-right">${formatNumber(cashFlowData.netChangeInCash)}</td></tr>
            <tr><td>${t.beginningCash}</td><td class="text-right">${formatNumber(cashFlowData.beginningCashBalance)}</td></tr>
            <tr class="grand-total"><td>${t.endingCash}</td><td class="text-right">${formatNumber(cashFlowData.endingCashBalance)}</td></tr>
          </table>
        ` : ''}
      </body></html>`;
    const w = window.open('', '_blank', 'width=900,height=700');
    if (w) {
      w.document.write(content);
      w.document.close();
      w.focus();
      setTimeout(() => { w.print(); }, 500);
    }
  };

  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Assessment sx={{ fontSize: 36, color: 'primary.main' }} />
          <Box>
            <Typography variant="h5" fontWeight="bold">{t.title}</Typography>
            <Typography variant="body2" color="textSecondary">{t.subtitle}</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Excel"><IconButton onClick={exportToExcel} color="success" disabled={!incomeData}><ExcelIcon /></IconButton></Tooltip>
          <Tooltip title="PDF"><IconButton onClick={exportToPDF} color="error" disabled={!incomeData}><PdfIcon /></IconButton></Tooltip>
          <Tooltip title="Print"><IconButton onClick={handlePrint} color="info" disabled={!incomeData}><PrintIcon /></IconButton></Tooltip>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <Autocomplete
            size="small"
            options={fiscalPeriods}
            getOptionLabel={(o) => o.periodName}
            value={fiscalPeriods.find(p => p.fiscalPeriodId === fiscalPeriodId) || null}
            onChange={(_, v) => {
              if (v) {
                setFiscalPeriodId(v.fiscalPeriodId);
                setFromDate(v.startDate.split('T')[0]);
                setToDate(v.endDate.split('T')[0]);
              } else {
                setFiscalPeriodId(undefined);
              }
            }}
            renderInput={(params) => <TextField {...params} label={t.fiscalPeriod} />}
            sx={{ minWidth: 200 }}
          />
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t.fromDate}
              value={fromDate ? new Date(fromDate) : null}
              onChange={(date) => {
                const newValue = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                setFromDate(newValue);
                if (toDate && newValue && toDate < newValue) setToDate('');
              }}
              format="dd/MM/yyyy"
              views={['year', 'month', 'day']}
              openTo="day"
              minDate={minDate}
              maxDate={toDate ? new Date(toDate) : maxDate}
              slotProps={{ textField: { size: 'small', fullWidth: true, sx: { width: 170 } } }}
            />
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t.toDate}
              value={toDate ? new Date(toDate) : null}
              onChange={(date) => {
                const newValue = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                setToDate(newValue);
                if (fromDate && newValue && fromDate > newValue) setFromDate('');
              }}
              format="dd/MM/yyyy"
              views={['year', 'month', 'day']}
              openTo="day"
              minDate={fromDate ? new Date(fromDate) : minDate}
              maxDate={maxDate}
              slotProps={{ textField: { size: 'small', fullWidth: true, sx: { width: 170 } } }}
            />
          </LocalizationProvider>
          <Button variant="contained" onClick={generateReports} startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />} disabled={loading}>
            {t.refresh}
          </Button>
        </Box>
      </Paper>

      {/* Export Options - Premium Design */}
      <Fade in={!!incomeData}>
        <Paper sx={{ 
          p: 2.5, 
          mb: 3, 
          background: isDark 
            ? 'linear-gradient(135deg, rgba(25, 118, 210, 0.1) 0%, rgba(156, 39, 176, 0.1) 100%)' 
            : 'linear-gradient(135deg, rgba(25, 118, 210, 0.05) 0%, rgba(156, 39, 176, 0.05) 100%)',
          border: '1px solid',
          borderColor: isDark ? 'rgba(25, 118, 210, 0.3)' : 'rgba(25, 118, 210, 0.2)',
          borderRadius: 2,
          display: incomeData ? 'block' : 'none'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ 
                width: 40, 
                height: 40, 
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #1976d2 0%, #9c27b0 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)'
              }}>
                <FileDownload sx={{ color: 'white', fontSize: 22 }} />
              </Box>
              <Typography variant="h6" fontWeight="bold" sx={{ 
                background: 'linear-gradient(135deg, #1976d2 0%, #9c27b0 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                {t.exportOptions}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {t.selectStatements}
            </Typography>
          </Box>

          <Divider sx={{ mb: 2, borderColor: 'rgba(25, 118, 210, 0.2)' }} />

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
            {/* Income Statement */}
            <Card 
              onClick={() => setExportOptions(prev => ({ ...prev, incomeStatement: !prev.incomeStatement }))}
              sx={{ 
                minWidth: 200,
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                border: '2px solid',
                borderColor: exportOptions.incomeStatement ? '#4caf50' : 'transparent',
                background: exportOptions.incomeStatement 
                  ? 'linear-gradient(135deg, rgba(76, 175, 80, 0.15) 0%, rgba(56, 142, 60, 0.15) 100%)'
                  : isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                boxShadow: exportOptions.incomeStatement 
                  ? '0 8px 24px rgba(76, 175, 80, 0.25)' 
                  : '0 2px 8px rgba(0,0,0,0.1)',
                transform: exportOptions.incomeStatement ? 'translateY(-4px) scale(1.02)' : 'none',
                '&:hover': {
                  transform: 'translateY(-4px) scale(1.02)',
                  boxShadow: '0 12px 28px rgba(76, 175, 80, 0.3)',
                  borderColor: exportOptions.incomeStatement ? '#4caf50' : 'rgba(76, 175, 80, 0.5)'
                }
              }}
            >
              <CardContent sx={{ textAlign: 'center', py: 2, px: 3, position: 'relative' }}>
                {exportOptions.incomeStatement && (
                  <Box sx={{ 
                    position: 'absolute', 
                    top: 8, 
                    right: 8,
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(76, 175, 80, 0.4)',
                    animation: 'scaleIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)'
                  }}>
                    <CheckCircle sx={{ color: 'white', fontSize: 18 }} />
                  </Box>
                )}
                <PieChart sx={{ 
                  fontSize: 48, 
                  mb: 1,
                  color: exportOptions.incomeStatement ? '#4caf50' : 'text.secondary',
                  filter: exportOptions.incomeStatement ? 'drop-shadow(0 4px 8px rgba(76, 175, 80, 0.3))' : 'none',
                  transition: 'all 0.3s'
                }} />
                <Typography variant="subtitle1" fontWeight="bold" sx={{ 
                  color: exportOptions.incomeStatement ? '#4caf50' : 'text.primary',
                  transition: 'color 0.3s'
                }}>
                  {t.incomeStatement}
                </Typography>
                <Chip 
                  label={exportOptions.incomeStatement ? '✓ ' + t.selectAll.split(' ')[0] : t.selectAll.split(' ')[1]} 
                  size="small"
                  sx={{ 
                    mt: 1,
                    background: exportOptions.incomeStatement 
                      ? 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)' 
                      : 'rgba(0,0,0,0.1)',
                    color: exportOptions.incomeStatement ? 'white' : 'text.secondary',
                    fontWeight: 'bold',
                    fontSize: '0.7rem'
                  }}
                />
              </CardContent>
            </Card>

            {/* Balance Sheet */}
            <Card 
              onClick={() => setExportOptions(prev => ({ ...prev, balanceSheet: !prev.balanceSheet }))}
              sx={{ 
                minWidth: 200,
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                border: '2px solid',
                borderColor: exportOptions.balanceSheet ? '#2196f3' : 'transparent',
                background: exportOptions.balanceSheet 
                  ? 'linear-gradient(135deg, rgba(33, 150, 243, 0.15) 0%, rgba(25, 118, 210, 0.15) 100%)'
                  : isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                boxShadow: exportOptions.balanceSheet 
                  ? '0 8px 24px rgba(33, 150, 243, 0.25)' 
                  : '0 2px 8px rgba(0,0,0,0.1)',
                transform: exportOptions.balanceSheet ? 'translateY(-4px) scale(1.02)' : 'none',
                '&:hover': {
                  transform: 'translateY(-4px) scale(1.02)',
                  boxShadow: '0 12px 28px rgba(33, 150, 243, 0.3)',
                  borderColor: exportOptions.balanceSheet ? '#2196f3' : 'rgba(33, 150, 243, 0.5)'
                }
              }}
            >
              <CardContent sx={{ textAlign: 'center', py: 2, px: 3, position: 'relative' }}>
                {exportOptions.balanceSheet && (
                  <Box sx={{ 
                    position: 'absolute', 
                    top: 8, 
                    right: 8,
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(33, 150, 243, 0.4)',
                    animation: 'scaleIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)'
                  }}>
                    <CheckCircle sx={{ color: 'white', fontSize: 18 }} />
                  </Box>
                )}
                <BarChart sx={{ 
                  fontSize: 48, 
                  mb: 1,
                  color: exportOptions.balanceSheet ? '#2196f3' : 'text.secondary',
                  filter: exportOptions.balanceSheet ? 'drop-shadow(0 4px 8px rgba(33, 150, 243, 0.3))' : 'none',
                  transition: 'all 0.3s'
                }} />
                <Typography variant="subtitle1" fontWeight="bold" sx={{ 
                  color: exportOptions.balanceSheet ? '#2196f3' : 'text.primary',
                  transition: 'color 0.3s'
                }}>
                  {t.balanceSheet}
                </Typography>
                <Chip 
                  label={exportOptions.balanceSheet ? '✓ ' + t.selectAll.split(' ')[0] : t.selectAll.split(' ')[1]} 
                  size="small"
                  sx={{ 
                    mt: 1,
                    background: exportOptions.balanceSheet 
                      ? 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)' 
                      : 'rgba(0,0,0,0.1)',
                    color: exportOptions.balanceSheet ? 'white' : 'text.secondary',
                    fontWeight: 'bold',
                    fontSize: '0.7rem'
                  }}
                />
              </CardContent>
            </Card>

            {/* Cash Flow */}
            <Card 
              onClick={() => setExportOptions(prev => ({ ...prev, cashFlow: !prev.cashFlow }))}
              sx={{ 
                minWidth: 200,
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                border: '2px solid',
                borderColor: exportOptions.cashFlow ? '#ff9800' : 'transparent',
                background: exportOptions.cashFlow 
                  ? 'linear-gradient(135deg, rgba(255, 152, 0, 0.15) 0%, rgba(245, 124, 0, 0.15) 100%)'
                  : isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                boxShadow: exportOptions.cashFlow 
                  ? '0 8px 24px rgba(255, 152, 0, 0.25)' 
                  : '0 2px 8px rgba(0,0,0,0.1)',
                transform: exportOptions.cashFlow ? 'translateY(-4px) scale(1.02)' : 'none',
                '&:hover': {
                  transform: 'translateY(-4px) scale(1.02)',
                  boxShadow: '0 12px 28px rgba(255, 152, 0, 0.3)',
                  borderColor: exportOptions.cashFlow ? '#ff9800' : 'rgba(255, 152, 0, 0.5)'
                }
              }}
            >
              <CardContent sx={{ textAlign: 'center', py: 2, px: 3, position: 'relative' }}>
                {exportOptions.cashFlow && (
                  <Box sx={{ 
                    position: 'absolute', 
                    top: 8, 
                    right: 8,
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(255, 152, 0, 0.4)',
                    animation: 'scaleIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)'
                  }}>
                    <CheckCircle sx={{ color: 'white', fontSize: 18 }} />
                  </Box>
                )}
                <SwapVert sx={{ 
                  fontSize: 48, 
                  mb: 1,
                  color: exportOptions.cashFlow ? '#ff9800' : 'text.secondary',
                  filter: exportOptions.cashFlow ? 'drop-shadow(0 4px 8px rgba(255, 152, 0, 0.3))' : 'none',
                  transition: 'all 0.3s'
                }} />
                <Typography variant="subtitle1" fontWeight="bold" sx={{ 
                  color: exportOptions.cashFlow ? '#ff9800' : 'text.primary',
                  transition: 'color 0.3s'
                }}>
                  {t.cashFlow}
                </Typography>
                <Chip 
                  label={exportOptions.cashFlow ? '✓ ' + t.selectAll.split(' ')[0] : t.selectAll.split(' ')[1]} 
                  size="small"
                  sx={{ 
                    mt: 1,
                    background: exportOptions.cashFlow 
                      ? 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)' 
                      : 'rgba(0,0,0,0.1)',
                    color: exportOptions.cashFlow ? 'white' : 'text.secondary',
                    fontWeight: 'bold',
                    fontSize: '0.7rem'
                  }}
                />
              </CardContent>
            </Card>
          </Box>

          {/* Quick Actions */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2.5 }}>
            <Button 
              size="small" 
              variant="outlined"
              onClick={() => setExportOptions({ incomeStatement: true, balanceSheet: true, cashFlow: true })}
              sx={{ 
                borderRadius: 2,
                borderColor: 'primary.main',
                color: 'primary.main',
                '&:hover': {
                  borderColor: 'primary.dark',
                  background: 'rgba(25, 118, 210, 0.08)'
                }
              }}
            >
              {t.selectAll}
            </Button>
            <Button 
              size="small" 
              variant="outlined"
              onClick={() => setExportOptions({ incomeStatement: false, balanceSheet: false, cashFlow: false })}
              sx={{ 
                borderRadius: 2,
                borderColor: 'error.main',
                color: 'error.main',
                '&:hover': {
                  borderColor: 'error.dark',
                  background: 'rgba(211, 47, 47, 0.08)'
                }
              }}
            >
              {t.deselectAll}
            </Button>
          </Box>

          {/* CSS Animation */}
          <style>
            {`
              @keyframes scaleIn {
                0% {
                  transform: scale(0);
                  opacity: 0;
                }
                50% {
                  transform: scale(1.2);
                }
                100% {
                  transform: scale(1);
                  opacity: 1;
                }
              }
            `}
          </style>
        </Paper>
      </Fade>

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="fullWidth" sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab icon={<PieChart />} label={t.incomeStatement} iconPosition="start" />
          <Tab icon={<BarChart />} label={t.balanceSheet} iconPosition="start" />
          <Tab icon={<SwapVert />} label={t.cashFlow} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress size={50} /></Box>
      ) : (
        <>
          <TabPanel value={activeTab} index={0}>{renderIncomeStatement()}</TabPanel>
          <TabPanel value={activeTab} index={1}>{renderBalanceSheet()}</TabPanel>
          <TabPanel value={activeTab} index={2}>{renderCashFlow()}</TabPanel>
        </>
      )}

      {/* Footer */}
      {incomeData && (
        <Box sx={{ mt: 2, textAlign: 'center', color: 'text.secondary' }}>
          <Typography variant="caption">{t.period}: {formatDate(fromDate)} - {formatDate(toDate)} | {t.generatedAt}: {new Date().toLocaleString(isRTL ? 'ar-KW' : 'en-GB')}</Typography>
        </Box>
      )}

      {/* ✅ Right-click context menu للحصول على كشف حساب تفصيلي/مختصر */}
      <AccountStatementContextMenu
        ref={acctMenuRef}
        companyInfo={companyInfo}
        isRTL={isRTL}
        minDate={fromDate ? new Date(fromDate) : undefined}
        maxDate={toDate ? new Date(toDate) : undefined}
        defaultFromDate={fromDate}
        defaultToDate={toDate}
      />
    </Box>
  );
};

export default FinancialStatementsPage;